// @ts-nocheck
import { useState, useEffect, useRef, createContext, useContext } from "react";
import * as XLSX from "xlsx";

// ─── TAAL / LANGUAGE ──────────────────────────────────────────────────
const LangCtx = createContext("nl");
const SetLangCtx = createContext(() => {});
const useLang = () => useContext(LangCtx);
const useSetLang = () => useContext(SetLangCtx);
const useT = () => {
  const lang = useLang();
  return (nl, en) => (lang === "nl" ? nl : en);
};
const getTL = (lang) => ({
  free: lang === "nl" ? "Vrij antwoord" : "Free answer",
  build: lang === "nl" ? "Voortbouwen op ander" : "Build on other",
  perspective: lang === "nl" ? "Ander perspectief" : "Different perspective",
});
const useTL = () => getTL(useLang());

// ─── FIREBASE ────────────────────────────────────────────────────────
const FIREBASE_URL =
  "https://brainwriting-b4903-default-rtdb.europe-west1.firebasedatabase.app";
const SECRET = "brainwriteTTC";

function keyToPath(k: string) {
  if (k.startsWith("sess:")) return `${SECRET}/sessions/${k.slice(5)}`;
  if (k.startsWith("ans:")) return `${SECRET}/answers/${k.slice(4)}`;
  return k;
}

const S = {
  async get(k: string) {
    try {
      const res = await fetch(`${FIREBASE_URL}/${keyToPath(k)}.json`);
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.error("FB get:", e);
      return null;
    }
  },
  async set(k: string, v: any) {
    try {
      await fetch(`${FIREBASE_URL}/${keyToPath(k)}.json`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(v),
      });
    } catch (e) {
      console.error("FB set:", e);
    }
  },
  async del(k: string) {
    try {
      await fetch(`${FIREBASE_URL}/${keyToPath(k)}.json`, { method: "DELETE" });
    } catch (e) {
      console.error("FB del:", e);
    }
  },
  async list() {
    try {
      const res = await fetch(
        `${FIREBASE_URL}/${SECRET}/sessions.json?shallow=true`
      );
      if (!res.ok) return [];
      const data = await res.json();
      return data ? Object.keys(data).map((k: string) => `sess:${k}`) : [];
    } catch {
      return [];
    }
  },
};

const normalizeArr = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data.filter(Boolean);
  return Object.values(data).filter(Boolean);
};
const normalizeSession = (sess) => {
  if (!sess) return null;
  return {
    ...sess,
    rounds: normalizeArr(sess.rounds),
    participants: normalizeArr(sess.participants),
  };
};

// ─── HELPERS ─────────────────────────────────────────────────────────
const uid = () => Math.random().toString(36).slice(2, 10);
const randomCode = () => Math.random().toString(36).slice(2, 8).toUpperCase();

const msRemaining = (sess) => {
  if (!sess?.rounds?.length) return 0;
  const total = (sess.rounds[sess.roundIndex]?.seconds ?? 20) * 1000;
  if (sess.roundState === "running")
    return Math.max(
      0,
      total - (sess.elapsed || 0) * 1000 - (Date.now() - sess.startedAt)
    );
  if (sess.roundState === "paused")
    return Math.max(0, total - (sess.elapsed || 0) * 1000);
  if (sess.roundState === "done") return 0;
  return total;
};

const formatTime = (ms) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(s / 60),
    sec = s % 60;
  if (m === 0) return `${sec}s`;
  return sec === 0 ? `${m}m` : `${m}m ${String(sec).padStart(2, "0")}s`;
};
const formatSec = (s) => {
  const m = Math.floor(s / 60),
    sec = s % 60;
  if (m === 0) return `${s}s`;
  return sec === 0 ? `${m} min` : `${m}m ${sec}s`;
};

const getBuildSource = (sess, answers, myId) => {
  if (!sess || sess.roundIndex === 0) return null;
  const prevIndex = sess.roundIndex - 1;
  const prev = answers.filter((a) => Number(a.roundIndex) === prevIndex);
  if (prev.length === 0) return null;
  const sorted = [...(sess.participants || [])].sort(
    (a, b) => a.joinedAt - b.joinedAt
  );
  const myPos = sorted.findIndex((p) => p.id === myId);
  if (myPos === -1) return null;
  const srcId = sorted[(myPos - 1 + sorted.length) % sorted.length]?.id;
  return prev.find((a) => String(a.participantId) === String(srcId)) || null;
};

// ─── DEMO SESSIES ─────────────────────────────────────────────────────
const DEMO_SESSIONS = [
  {
    title: "Productinnovatie Demo",
    rounds: [
      {
        question:
          "Welk nieuw product of dienst zou jouw organisatie kunnen lanceren om klanten beter te helpen?",
        type: "free",
        hint: "",
        seconds: 20,
      },
      {
        question:
          "Bouw voort op het idee van je collega en maak het concreter.",
        type: "build",
        hint: "",
        seconds: 20,
      },
      {
        question:
          "Bekijk het idee vanuit het perspectief van een sceptische klant.",
        type: "perspective",
        hint: "Stel je voor: je bent een klant die dit product nog nooit heeft gebruikt.",
        seconds: 20,
      },
    ],
  },
  {
    title: "Teamverbetering Demo",
    rounds: [
      {
        question:
          "Wat is de grootste belemmering voor effectief samenwerken in ons team?",
        type: "free",
        hint: "",
        seconds: 20,
      },
      {
        question:
          "Bouw voort op de belemmering van je collega: welke oplossing zie jij?",
        type: "build",
        hint: "",
        seconds: 20,
      },
    ],
  },
  {
    title: "Klantbeleving Demo",
    rounds: [
      {
        question:
          "Op welk moment in de klantreis laten we de meeste kansen liggen?",
        type: "free",
        hint: "",
        seconds: 20,
      },
      {
        question:
          "Bedenk een onverwachte manier om de klantbeleving te verbeteren.",
        type: "perspective",
        hint: "Denk als een klant die voor de eerste keer contact heeft met ons.",
        seconds: 20,
      },
      {
        question: "Bouw voort op het idee van je collega.",
        type: "build",
        hint: "",
        seconds: 20,
      },
    ],
  },
];

// ─── KLEUREN ─────────────────────────────────────────────────────────
const C = {
  bg: "#F2A7D8",
  surface: "#C724AE",
  card: "#C724AE",
  border: "#E087CC",
  input: "#B5179E",
  yellow: "#FFD700",
  neonblue: "#A855F7",
  red: "#e8442a",
  pink: "#B5179E",
  green: "#3dbe3d",
  text: "#ffffff",
  muted: "#F9C8E8",
};
const DT = "#3D0040";
const DM = "#8A1070";
const TC = { free: "#ffffff", build: "#FFD700", perspective: "#A855F7" };
const accent = "#D946EF";
const sub = "#B5179E";
const TI = { free: "◆", build: "⊕", perspective: "◎" };

const TIME_OPTS = [
  { l: "15s", v: 15 },
  { l: "20s", v: 20 },
  { l: "30s", v: 30 },
  { l: "45s", v: 45 },
  { l: "1m", v: 60 },
  { l: "90s", v: 90 },
  { l: "2m", v: 120 },
  { l: "3m", v: 180 },
  { l: "5m", v: 300 },
];

// ─── CONTROL BAR (Taal + Thema) ──────────────────────────────────────
function ControlBar() {
  const lang = useLang();
  const setLang = useSetLang();
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={() => setLang((l) => (l === "nl" ? "en" : "nl"))}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 9999,
        padding: "6px 14px",
        borderRadius: 100,
        background: hov ? "#D946EF55" : "#D946EF22",
        border: "2px solid #D946EF",
        color: "#D946EF",
        fontFamily: "'Montserrat',sans-serif",
        fontWeight: 800,
        fontSize: 12,
        cursor: "pointer",
        letterSpacing: 1.5,
        transition: "all .15s",
        backdropFilter: "blur(6px)",
      }}
    >
      {lang === "nl" ? "EN" : "NL"}
    </button>
  );
}

// ─── UI COMPONENTEN ───────────────────────────────────────────────────
function Btn({
  children,
  onClick,
  color = C.yellow,
  textColor = null,
  disabled = false,
  full = false,
  ghost = false,
  size = "md",
}) {
  const [hov, setHov] = useState(false);
  const pad =
    size === "sm" ? "6px 13px" : size === "lg" ? "14px 32px" : "9px 18px";
  const fs = size === "sm" ? 11 : size === "lg" ? 15 : 13;
  const isLg = size === "lg";
  const defaultColor = ghost
    ? hov
      ? "#ffffff"
      : color
    : isLg
    ? "#ffffff"
    : hov
    ? "#ffffff"
    : color;
  const resolvedTextColor = textColor
    ? hov
      ? "#ffffff"
      : textColor
    : defaultColor;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: full ? "100%" : "auto",
        padding: pad,
        fontSize: fs,
        fontFamily: "'Montserrat',sans-serif",
        fontWeight: 700,
        borderRadius: 8,
        border: `2px solid ${color}`,
        background: ghost
          ? hov
            ? color
            : "transparent"
          : isLg
          ? color
          : hov
          ? color
          : color + "33",
        color: resolvedTextColor,
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "all .15s",
        boxShadow: hov ? `0 0 20px ${color}80, 0 0 40px ${color}30` : "none",
        transform: hov && !disabled ? "translateY(-3px)" : "none",
      }}
    >
      {children}
    </button>
  );
}

function Card({ children, style = {} }) {
  return (
    <div
      style={{
        background: C.card,
        border: `1.5px solid ${C.border}`,
        borderRadius: 14,
        padding: 20,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function Clouds() {
  return (
    <>
      <svg
        style={{
          position: "fixed",
          top: 10,
          left: 0,
          opacity: 0.5,
          pointerEvents: "none",
          zIndex: 0,
        }}
        width="300"
        height="110"
        viewBox="0 0 300 110"
      >
        <ellipse cx="110" cy="75" rx="100" ry="40" fill="white" />
        <ellipse cx="80" cy="58" rx="70" ry="45" fill="white" />
        <ellipse cx="155" cy="55" rx="65" ry="42" fill="white" />
        <ellipse cx="220" cy="68" rx="75" ry="38" fill="white" />
      </svg>
      <svg
        style={{
          position: "fixed",
          top: 5,
          right: 0,
          opacity: 0.45,
          pointerEvents: "none",
          zIndex: 0,
        }}
        width="260"
        height="100"
        viewBox="0 0 260 100"
      >
        <ellipse cx="130" cy="70" rx="95" ry="36" fill="white" />
        <ellipse cx="100" cy="52" rx="65" ry="42" fill="white" />
        <ellipse cx="175" cy="48" rx="60" ry="38" fill="white" />
      </svg>
      <svg
        style={{
          position: "fixed",
          bottom: 20,
          right: 10,
          opacity: 0.35,
          pointerEvents: "none",
          zIndex: 0,
        }}
        width="220"
        height="85"
        viewBox="0 0 220 85"
      >
        <ellipse cx="110" cy="60" rx="85" ry="32" fill="white" />
        <ellipse cx="80" cy="42" rx="58" ry="36" fill="white" />
        <ellipse cx="150" cy="38" rx="52" ry="32" fill="white" />
      </svg>
    </>
  );
}

function Ball({ style = {}, size, color, highlight, zIndex = 0 }) {
  const id = `b${color.replace(/[^a-zA-Z0-9]/g, "")}${size}`;
  return (
    <svg
      style={{
        position: "fixed",
        pointerEvents: "none",
        zIndex,
        overflow: "visible",
        ...style,
      }}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
    >
      <defs>
        <radialGradient id={id} cx="35%" cy="28%" r="65%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.45" />
          <stop offset="20%" stopColor={highlight} stopOpacity="0.85" />
          <stop offset="65%" stopColor={color} stopOpacity="1" />
          <stop offset="100%" stopColor={color} stopOpacity="0.75" />
        </radialGradient>
        <radialGradient id={`${id}sh`} cx="50%" cy="85%" r="40%">
          <stop offset="0%" stopColor="#00000035" />
          <stop offset="100%" stopColor="transparent" />
        </radialGradient>
      </defs>
      <circle
        cx={size / 2}
        cy={size / 2}
        r={size / 2 - 1}
        fill={`url(#${id})`}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={size / 2 - 1}
        fill={`url(#${id}sh)`}
      />
    </svg>
  );
}

function Badge({ color, children }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 12px",
        borderRadius: 100,
        background: color + "25",
        color,
        border: `1px solid ${color}50`,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.8,
        textTransform: "uppercase",
        fontFamily: "'Montserrat',sans-serif",
      }}
    >
      {children}
    </span>
  );
}

function ProgressBar({ current, total }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span
        style={{
          fontSize: 12,
          color: C.muted,
          whiteSpace: "nowrap",
          fontFamily: "'Montserrat',sans-serif",
          fontWeight: 600,
        }}
      >
        {current + 1} / {total}
      </span>
      <div
        style={{
          flex: 1,
          height: 4,
          background: C.surface,
          borderRadius: 2,
          border: `1px solid ${C.border}`,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${((current + 1) / total) * 100}%`,
            background: `linear-gradient(90deg,${C.neonblue},${C.yellow})`,
            borderRadius: 2,
            transition: "width .4s",
          }}
        />
      </div>
    </div>
  );
}

function InputField({
  label,
  value,
  onChange,
  placeholder,
  multiline = false,
  rows = 3,
  mono = false,
}) {
  const base = {
    width: "100%",
    padding: "10px 14px",
    background: C.input,
    border: `1.5px solid ${C.border}`,
    borderRadius: 8,
    color: "#ffffff",
    fontSize: 14,
    fontFamily: mono ? "monospace" : "'Montserrat',sans-serif",
    marginTop: 6,
    outline: "none",
    boxSizing: "border-box",
    display: "block",
    fontWeight: 500,
  };
  return (
    <div style={{ marginBottom: 16 }}>
      {label && (
        <label
          style={{
            fontSize: 11,
            color: C.muted,
            textTransform: "uppercase",
            letterSpacing: 1.5,
            fontFamily: "'Montserrat',sans-serif",
            fontWeight: 700,
          }}
        >
          {label}
        </label>
      )}
      {multiline ? (
        <textarea
          rows={rows}
          spellCheck={false}
          style={{ ...base, resize: "vertical" }}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
      ) : (
        <input
          style={base}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

function InputFieldLight({
  label,
  value,
  onChange,
  placeholder,
  multiline = false,
  rows = 3,
  mono = false,
}) {
  const base = {
    width: "100%",
    padding: "10px 14px",
    background: C.input,
    border: `1.5px solid ${C.border}`,
    borderRadius: 8,
    color: "#ffffff",
    WebkitTextFillColor: "#ffffff",
    fontSize: 14,
    fontFamily: mono ? "monospace" : "'Montserrat',sans-serif",
    marginTop: 6,
    outline: "none",
    boxSizing: "border-box",
    display: "block",
    fontWeight: 500,
  };
  return (
    <div style={{ marginBottom: 16 }}>
      {label && (
        <label
          style={{
            fontSize: 11,
            color: DT,
            textTransform: "uppercase",
            letterSpacing: 1.5,
            fontFamily: "'Montserrat',sans-serif",
            fontWeight: 700,
          }}
        >
          {label}
        </label>
      )}
      {multiline ? (
        <textarea
          rows={rows}
          spellCheck={false}
          className="light-input"
          style={{ ...base, resize: "vertical" }}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
      ) : (
        <input
          className="light-input"
          style={base}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

function Spinner({ color = null }) {
  const col = color ?? C.yellow;
  return (
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        border: `3px solid ${color}`,
        borderTopColor: "transparent",
        animation: "spin 1s linear infinite",
        margin: "0 auto 24px",
      }}
    />
  );
}

function Clock({ sess }) {
  const t = useT();
  const [ms, setMs] = useState(() => msRemaining(sess));

  useEffect(() => {
    setMs(msRemaining(sess));
    if (sess?.roundState !== "running") return;
    const id = setInterval(() => setMs(msRemaining(sess)), 200);
    return () => clearInterval(id);
  }, [sess?.roundState, sess?.startedAt, sess?.elapsed, sess?.roundIndex]);

  const total = (sess?.rounds?.[sess?.roundIndex]?.seconds ?? 20) * 1000;
  const pct = total > 0 ? Math.min(1, 1 - ms / total) : 0;
  const low = ms < 10000 && ms > 0 && sess?.roundState === "running";
  const critical = ms < 5000 && ms > 0 && sess?.roundState === "running";
  const col = critical ? C.red : C.yellow;

  const LBL = {
    running: t("Loopt", "Running"),
    paused: t("Gepauzeerd", "Paused"),
    done: t("Afgerond", "Finished"),
    waiting: t("Wacht op start", "Waiting to start"),
  };

  return (
    <div>
      <div
        style={{
          fontFamily: "'Montserrat',sans-serif",
          fontSize: 52,
          fontWeight: 800,
          color: col,
          lineHeight: 1,
          transition: "color .3s",
          animation: critical
            ? "blink 0.5s ease infinite"
            : low
            ? "pulse 1s ease infinite"
            : "none",
          textShadow: critical ? `0 0 40px ${C.red}80` : `0 0 30px ${col}50`,
        }}
      >
        {formatTime(ms)}
      </div>
      <div
        style={{
          height: 7,
          background: C.surface,
          borderRadius: 3,
          marginTop: 10,
          overflow: "hidden",
          border: `1px solid ${C.border}`,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct * 100}%`,
            background: critical
              ? `linear-gradient(90deg,${C.red},${C.yellow})`
              : low
              ? `linear-gradient(90deg,${C.yellow},${C.red})`
              : `linear-gradient(90deg,${C.neonblue},${C.yellow})`,
            borderRadius: 3,
            transition: "width .25s linear",
          }}
        />
      </div>
      <div
        style={{
          fontSize: 10,
          color: C.muted,
          marginTop: 5,
          textTransform: "uppercase",
          letterSpacing: 1.5,
          fontFamily: "'Montserrat',sans-serif",
          fontWeight: 600,
        }}
      >
        {LBL[sess?.roundState] ?? "—"}
      </div>
    </div>
  );
}

// ─── EXCEL EXPORT ─────────────────────────────────────────────────────
function downloadExcel(sess, answers, lang = "nl") {
  const TL = getTL(lang);
  const isNl = lang === "nl";
  const rows = [];
  sess.rounds.forEach((r, i) => {
    const ra = answers.filter((a) => Number(a.roundIndex) === i);
    (ra.length > 0 ? ra : [null]).forEach((a) => {
      rows.push({
        [isNl ? "Sessie" : "Session"]: sess.title,
        [isNl ? "Code" : "Code"]: sess.code,
        [isNl ? "Ronde" : "Round"]: i + 1,
        [isNl ? "Type" : "Type"]: TL[r.type],
        [isNl ? "Vraag" : "Question"]: r.question,
        [isNl ? "Perspectief" : "Perspective"]: r.hint || "",
        [isNl ? "Tijdslimiet" : "Time limit"]: formatSec(r.seconds),
        [isNl ? "Deelnemer" : "Participant"]:
          a?.participantName ?? (isNl ? "(geen antwoord)" : "(no answer)"),
        [isNl ? "Antwoord" : "Answer"]: a?.answer ?? "",
        [isNl ? "Tijdstip" : "Timestamp"]: a
          ? new Date(a.timestamp).toLocaleString(isNl ? "nl-NL" : "en-GB")
          : "",
      });
    });
  });
  const ws = XLSX.utils.json_to_sheet(rows);
  ws["!cols"] = [16, 10, 7, 20, 40, 25, 12, 15, 50, 20].map((w) => ({
    wch: w,
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, isNl ? "Antwoorden" : "Answers");
  XLSX.writeFile(wb, `BrainWrite_${sess.title.replace(/\s+/g, "_")}.xlsx`);
}

// ─── WAAROM SECTIE ────────────────────────────────────────────────────
function WaaromSection() {
  const [open, setOpen] = useState(false);
  const t = useT();
  const items = [
    t(
      "Iedereen doet mee, niet alleen de luidste stemmen in de ruimte",
      "Everyone participates, not just the loudest voices in the room"
    ),
    t(
      "Anonimiteit geeft vrijheid voor eerlijkere en verrassendere antwoorden",
      "Anonymity creates freedom for more honest and surprising answers"
    ),
    t(
      "Live en betrokken, geen saai formulier maar een echte gezamenlijke ervaring",
      "Live and engaging, not a boring form but a real shared experience"
    ),
    t(
      "Direct bruikbare output, download alles in Excel en ga meteen aan de slag",
      "Directly usable output, download everything in Excel and get started right away"
    ),
    t(
      "Geen skills nodig, alleen goede vragen, een beetje tijd en iemands eigen verbeelding",
      "No skills needed, just good questions, a little time and someone's imagination"
    ),
  ];
  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          background: C.bg,
          border: `1.5px solid ${DT}25`,
          borderRadius: 12,
          padding: "12px 20px",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontFamily: "'Montserrat',sans-serif",
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 700, color: DT }}>
          {t("Waarom werkt brainwriting?", "Why does brainwriting work?")}
        </span>
        <span
          style={{
            fontSize: 16,
            color: DT,
            display: "inline-block",
            transition: "transform .2s",
            transform: open ? "rotate(180deg)" : "none",
          }}
        >
          ▾
        </span>
      </button>
      {open && (
        <div
          style={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            textAlign: "left",
          }}
        >
          {items.map((text) => (
            <div
              key={text}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                padding: "10px 14px",
                background: C.card + "70",
                borderRadius: 10,
                border: `1px solid ${C.border}30`,
              }}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                style={{ flexShrink: 0, marginTop: 4 }}
              >
                <circle cx="5" cy="5" r="5" fill={accent} />
              </svg>
              <span
                style={{
                  fontSize: 13,
                  color: C.muted,
                  fontFamily: "'Montserrat',sans-serif",
                  lineHeight: 1.55,
                  fontWeight: 500,
                }}
              >
                {text}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── LANDING ─────────────────────────────────────────────────────────
function Landing({ goFacilitator, goJoin, goResume }) {
  const t = useT();
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        textAlign: "center",
        position: "relative",
        background: `
        radial-gradient(ellipse 80% 40% at 50% 0%, #ffffff60, transparent),
        radial-gradient(ellipse 60% 30% at 20% 30%, #ffffff40, transparent),
        radial-gradient(ellipse 50% 25% at 80% 20%, #ffffff35, transparent),
        radial-gradient(ellipse 40% 20% at 60% 10%, #ffffff30, transparent),
        ${C.bg}`,
      }}
    >
      <Clouds />
      <Ball
        size={62}
        color={C.red}
        highlight="#ff8c8c"
        style={{ bottom: 70, left: 50 }}
      />
      <Ball
        size={40}
        color={C.yellow}
        highlight="#fff0a0"
        style={{ bottom: 140, left: 140 }}
      />
      <Ball
        size={52}
        color="#7B4FBE"
        highlight="#c49be8"
        style={{ bottom: 55, right: 70 }}
      />
      <Ball
        size={36}
        color={C.green}
        highlight="#7ddd7d"
        style={{ top: 100, right: 120 }}
      />
      <Ball
        size={26}
        color={C.pink}
        highlight="#ffb8d4"
        style={{ top: 160, left: 80 }}
      />
      <Ball
        size={22}
        color={C.neonblue}
        highlight="#a8f0fa"
        style={{ bottom: 190, right: 170 }}
      />

      <div
        style={{
          maxWidth: 760,
          width: "100%",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div
          style={{ position: "absolute", top: 0, right: 0, zIndex: 10 }}
        ></div>
        <div
          style={{
            display: "inline-block",
            padding: "7px 24px",
            borderRadius: 100,
            background: DT + "18",
            border: `1.5px solid ${DT}70`,
            fontSize: 13,
            color: DT,
            letterSpacing: 5,
            textTransform: "uppercase",
            marginBottom: 40,
            fontWeight: 700,
            fontFamily: "'Montserrat',sans-serif",
          }}
        >
          {t("Creatief denken in groepen", "Creative thinking in groups")}
        </div>

        <h1
          style={{
            fontFamily: "'Montserrat',sans-serif",
            fontSize: "clamp(72px,13vw,148px)",
            fontWeight: 800,
            lineHeight: 0.88,
            marginBottom: 36,
            letterSpacing: -3,
          }}
        >
          <span
            style={{
              color: "#ffffff",
              textShadow: "0 0 30px #ffffff90, 0 0 60px #ffffff50",
            }}
          >
            Brain
          </span>
          <span
            style={{ color: C.yellow, textShadow: `0 0 12px ${C.yellow}40` }}
          >
            Write
          </span>
        </h1>

        <p
          style={{
            color: DT,
            fontSize: 17,
            lineHeight: 1.7,
            marginBottom: 56,
            fontFamily: "'Montserrat',sans-serif",
            fontWeight: 600,
          }}
        >
          {t(
            "Gestructureerde ideeënsessies voor teams.",
            "Structured idea sessions for teams."
          )}
          <br />
          {t(
            "Faciliteer rondes, bouw voort op elkaars input.",
            "Facilitate rounds, build on each other's input."
          )}
        </p>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 20,
            marginBottom: 18,
            width: "100%",
            alignItems: "stretch",
          }}
        >
          <button
            onClick={goFacilitator}
            style={{
              flex: 1,
              padding: "44px 32px",
              background: C.card,
              border: `2px solid ${C.border}`,
              borderRadius: 20,
              textAlign: "left",
              color: C.text,
              cursor: "pointer",
              fontFamily: "'Montserrat',sans-serif",
              transition: "all .18s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#ffffff";
              e.currentTarget.style.background = "#ffffff22";
              e.currentTarget.style.boxShadow = "0 0 24px #ffffff60";
              e.currentTarget.style.transform = "translateY(-4px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = C.border;
              e.currentTarget.style.background = C.card;
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.transform = "none";
            }}
          >
            <div style={{ fontSize: 34, color: C.yellow, marginBottom: 14 }}>
              ⊞
            </div>
            <div style={{ fontWeight: 800, fontSize: 19, marginBottom: 6 }}>
              {t("Sessie voorbereiden", "Set up a session")}
            </div>
            <div style={{ color: C.muted, fontSize: 13, fontWeight: 500 }}>
              {t("Maak een nieuwe sessie aan", "Create a new session")}
            </div>
          </button>

          <button
            onClick={goJoin}
            style={{
              flex: 1,
              padding: "44px 32px",
              background: C.card,
              border: `2px solid ${C.border}`,
              borderRadius: 20,
              textAlign: "left",
              color: C.text,
              cursor: "pointer",
              fontFamily: "'Montserrat',sans-serif",
              transition: "all .18s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = accent;
              e.currentTarget.style.background = accent + "22";
              e.currentTarget.style.boxShadow = `0 0 24px ${accent}60`;
              e.currentTarget.style.transform = "translateY(-4px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = C.border;
              e.currentTarget.style.background = C.card;
              e.currentTarget.style.boxShadow = "none";
              e.currentTarget.style.transform = "none";
            }}
          >
            <div style={{ fontSize: 34, color: accent, marginBottom: 14 }}>
              →
            </div>
            <div style={{ fontWeight: 800, fontSize: 19, marginBottom: 6 }}>
              {t("Deelnemen", "Join")}
            </div>
            <div style={{ color: C.muted, fontSize: 13, fontWeight: 500 }}>
              {t("Ik heb een sessiecode", "I have a session code")}
            </div>
          </button>
        </div>

        <button
          onClick={goResume}
          style={{
            width: "100%",
            background: C.pink + "25",
            border: `1.5px solid ${C.pink}`,
            borderRadius: 14,
            padding: "18px 28px",
            cursor: "pointer",
            color: C.pink,
            fontSize: 14,
            fontWeight: 700,
            marginBottom: 32,
            fontFamily: "'Montserrat',sans-serif",
            transition: "all .18s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = C.pink + "45";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = C.pink + "25";
          }}
        >
          🔑{" "}
          {t(
            "Eerder aangemaakte sessie hervatten",
            "Resume a previously created session"
          )}
        </button>

        <WaaromSection />
      </div>

      <p
        style={{
          position: "absolute",
          bottom: 48,
          left: 0,
          right: 0,
          textAlign: "center",
          fontSize: 13,
          color: DM,
          fontFamily: "'Montserrat',sans-serif",
          opacity: 0.6,
          padding: "0 24px",
        }}
      >
        🛡️ <strong style={{ fontWeight: 700 }}>Data Privacy:</strong>{" "}
        {t(
          "De data die je genereert is en blijft van jou. Think Tank Collective faciliteert alleen de technische omgeving.",
          "The data you generate is and remains yours. Think Tank Collective only facilitates the technical environment."
        )}
      </p>
    </div>
  );
}

// ─── ADMIN: SESSIE BEWERKEN ───────────────────────────────────────────
function AdminEdit({ sess: initSess, onBack, onSaved }) {
  const t = useT();
  const TL = useTL();
  const mkR = () => ({
    id: uid(),
    question: "",
    type: "free",
    hint: "",
    seconds: 20,
  });
  const [sess, setSess] = useState(initSess);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const updRound = (id, k, v) =>
    setSess((s) => ({
      ...s,
      rounds: s.rounds.map((r) => (r.id === id ? { ...r, [k]: v } : r)),
    }));
  const addRound = () =>
    setSess((s) => ({ ...s, rounds: [...s.rounds, mkR()] }));
  const remRound = (id) =>
    setSess((s) => ({ ...s, rounds: s.rounds.filter((r) => r.id !== id) }));

  const save = async () => {
    setBusy(true);
    await S.set(`sess:${sess.code}`, sess);
    setBusy(false);
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      onSaved(sess);
    }, 900);
  };

  return (
    <div
      style={{
        maxWidth: 700,
        margin: "0 auto",
        padding: "44px 24px 80px",
        position: "relative",
        zIndex: 1,
      }}
    >
      <button
        onClick={onBack}
        style={{
          background: "none",
          border: "none",
          color: DM,
          cursor: "pointer",
          fontSize: 13,
          fontFamily: "'Montserrat',sans-serif",
          fontWeight: 600,
          marginBottom: 32,
        }}
      >
        ← {t("Terug naar sessie", "Back to session")}
      </button>
      <h2
        style={{
          fontFamily: "'Montserrat',sans-serif",
          fontSize: 28,
          fontWeight: 800,
          marginBottom: 4,
          color: DT,
        }}
      >
        {t("Sessie bewerken", "Edit session")}
      </h2>
      <p
        style={{
          color: DM,
          fontSize: 13,
          marginBottom: 28,
          fontFamily: "'Montserrat',sans-serif",
        }}
      >
        {t("Code:", "Code:")}{" "}
        <strong style={{ color: C.yellow }}>{sess.code}</strong>
      </p>
      <InputField
        label={t("Sessienaam", "Session name")}
        value={sess.title}
        onChange={(e) => setSess((s) => ({ ...s, title: e.target.value }))}
      />
      <div style={{ marginTop: 16 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 14,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: DT,
              textTransform: "uppercase",
              letterSpacing: 1.5,
              fontFamily: "'Montserrat',sans-serif",
              fontWeight: 700,
            }}
          >
            {t("Rondes", "Rounds")} ({sess.rounds.length})
          </div>
          <Btn size="sm" color="#ffffff" textColor={DT} onClick={addRound}>
            + {t("Ronde toevoegen", "Add round")}
          </Btn>
        </div>
        {sess.rounds.map((r, i) => (
          <Card
            key={r.id}
            style={{ marginBottom: 12, borderColor: TC[r.type] + "60" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <Badge color={TC[r.type]}>
                {TI[r.type]} {t("Ronde", "Round")} {i + 1}
              </Badge>
              {sess.rounds.length > 1 && (
                <button
                  onClick={() => remRound(r.id)}
                  style={{
                    background: "none",
                    border: "none",
                    color: C.muted,
                    cursor: "pointer",
                    fontSize: 12,
                    fontFamily: "'Montserrat',sans-serif",
                  }}
                >
                  ✕ {t("Verwijder", "Remove")}
                </button>
              )}
            </div>
            <div
              style={{
                display: "flex",
                gap: 6,
                marginBottom: 12,
                flexWrap: "wrap",
              }}
            >
              {["free", "build", "perspective"].map((k) => (
                <button
                  key={k}
                  onClick={() => updRound(r.id, "type", k)}
                  style={{
                    padding: "3px 10px",
                    borderRadius: 100,
                    cursor: "pointer",
                    border: `1.5px solid ${r.type === k ? TC[k] : C.border}`,
                    background: r.type === k ? TC[k] + "25" : "transparent",
                    color: r.type === k ? TC[k] : C.muted,
                    fontSize: 11,
                    fontFamily: "'Montserrat',sans-serif",
                    fontWeight: 600,
                    transition: "all .12s",
                  }}
                >
                  {TI[k]} {TL[k]}
                </button>
              ))}
            </div>
            <InputField
              label={t("Vraag", "Question")}
              multiline
              rows={2}
              value={r.question}
              onChange={(e) => updRound(r.id, "question", e.target.value)}
              placeholder={
                r.type === "build"
                  ? t(
                      "Welk onderwerp bouwen deelnemers op voort?",
                      "What topic will participants build on?"
                    )
                  : r.type === "perspective"
                  ? t("Wat is de kernvraag?", "What is the core question?")
                  : t("Wat wil je weten?", "What do you want to know?")
              }
            />
            {r.type === "perspective" && (
              <InputField
                label={t("Perspectief-instructie", "Perspective instruction")}
                value={r.hint}
                onChange={(e) => updRound(r.id, "hint", e.target.value)}
                placeholder={t(
                  "Bijv: Beantwoord dit als klant",
                  "E.g.: Answer this as a customer"
                )}
              />
            )}
            <div>
              <div
                style={{
                  fontSize: 11,
                  color: C.muted,
                  textTransform: "uppercase",
                  letterSpacing: 1.5,
                  marginBottom: 8,
                  fontFamily: "'Montserrat',sans-serif",
                  fontWeight: 700,
                }}
              >
                {t("Tijdslimiet", "Time limit")}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 5,
                  flexWrap: "wrap",
                  marginBottom: 8,
                }}
              >
                {TIME_OPTS.map((o) => {
                  const sel = r.seconds === o.v;
                  return (
                    <button
                      key={o.v}
                      onClick={() => updRound(r.id, "seconds", o.v)}
                      style={{
                        padding: "4px 10px",
                        borderRadius: 6,
                        cursor: "pointer",
                        border: `1.5px solid ${sel ? C.yellow : C.border}`,
                        background: sel ? C.yellow + "25" : "transparent",
                        color: sel ? C.yellow : C.muted,
                        fontSize: 11,
                        fontFamily: "'Montserrat',sans-serif",
                        fontWeight: 600,
                      }}
                    >
                      {o.l}
                    </button>
                  );
                })}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    color: C.muted,
                    fontFamily: "'Montserrat',sans-serif",
                  }}
                >
                  {t("Of typ seconden:", "Or type seconds:")}
                </span>
                <input
                  type="number"
                  min={5}
                  max={300}
                  value={r.seconds}
                  onChange={(e) =>
                    updRound(
                      r.id,
                      "seconds",
                      Math.min(300, Math.max(5, parseInt(e.target.value) || 5))
                    )
                  }
                  style={{
                    width: 64,
                    padding: "4px 8px",
                    background: C.input,
                    border: `1.5px solid ${C.border}`,
                    borderRadius: 6,
                    color: "#ffffff",
                    fontSize: 12,
                    textAlign: "center",
                    fontFamily: "'Montserrat',sans-serif",
                    outline: "none",
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    color: C.yellow,
                    fontWeight: 700,
                    fontFamily: "'Montserrat',sans-serif",
                  }}
                >
                  = {formatSec(r.seconds)}
                </span>
              </div>
            </div>
          </Card>
        ))}
      </div>
      <div style={{ marginTop: 24 }}>
        <Btn
          size="lg"
          full
          color={saved ? C.green : accent}
          disabled={busy}
          onClick={save}
        >
          {saved
            ? `✓ ${t("Opgeslagen!", "Saved!")}`
            : busy
            ? t("Opslaan…", "Saving…")
            : t("Wijzigingen opslaan →", "Save changes →")}
        </Btn>
      </div>
    </div>
  );
}

// ─── ADMIN PANEL ──────────────────────────────────────────────────────
function Admin({ onBack, onOpenSession }) {
  const t = useT();
  const lang = useLang();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editSess, setEditSess] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [showTrash, setShowTrash] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [loginUser, setLoginUser] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginErr, setLoginErr] = useState("");

  const loadSessions = async () => {
    setLoading(true);
    const keys = await S.list();
    const all = await Promise.all(keys.map((k) => S.get(k)));
    setSessions(
      all
        .filter(Boolean)
        .map(normalizeSession)
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    );
    setLoading(false);
  };

  useEffect(() => {
    if (loggedIn) loadSessions();
  }, [loggedIn]);

  const handleLogin = () => {
    if (loginUser === "TTCDiego" && loginPass === "TTC2026") {
      setLoggedIn(true);
      setLoginErr("");
    } else
      setLoginErr(
        t(
          "Gebruikersnaam of wachtwoord onjuist.",
          "Incorrect username or password."
        )
      );
  };

  if (!loggedIn)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: `radial-gradient(ellipse 60% 50% at 50% 50%,${C.yellow}12,transparent),${C.bg}`,
        }}
      >
        <div style={{ width: "100%", maxWidth: 380 }}>
          <button
            onClick={onBack}
            style={{
              background: "none",
              border: "none",
              color: DM,
              cursor: "pointer",
              fontSize: 13,
              fontFamily: "'Montserrat',sans-serif",
              fontWeight: 600,
              marginBottom: 36,
            }}
          >
            ← {t("Terug", "Back")}
          </button>
          <div style={{ fontSize: 42, marginBottom: 16, textAlign: "center" }}>
            ⚙
          </div>
          <h2
            style={{
              fontFamily: "'Montserrat',sans-serif",
              fontSize: 28,
              fontWeight: 800,
              marginBottom: 6,
              color: DT,
              textAlign: "center",
            }}
          >
            Admin {t("login", "login")}
          </h2>
          <p
            style={{
              color: DM,
              fontSize: 13,
              marginBottom: 28,
              fontFamily: "'Montserrat',sans-serif",
              textAlign: "center",
            }}
          >
            {t("Alleen voor beheerders.", "For administrators only.")}
          </p>
          <InputFieldLight
            label={t("Gebruikersnaam", "Username")}
            value={loginUser}
            onChange={(e) => setLoginUser(e.target.value)}
            placeholder={t("Gebruikersnaam", "Username")}
          />
          <InputFieldLight
            label={t("Wachtwoord", "Password")}
            value={loginPass}
            onChange={(e) => setLoginPass(e.target.value)}
            placeholder={t("Wachtwoord", "Password")}
          />
          {loginErr && (
            <div
              style={{
                background: C.red + "20",
                border: `1px solid ${C.red}50`,
                borderRadius: 8,
                padding: "10px 14px",
                marginBottom: 14,
              }}
            >
              <p
                style={{
                  color: C.red,
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: "'Montserrat',sans-serif",
                }}
              >
                {loginErr}
              </p>
            </div>
          )}
          <Btn size="lg" full color={C.pink} onClick={handleLogin}>
            {t("Inloggen →", "Log in →")}
          </Btn>
        </div>
      </div>
    );

  if (editSess)
    return (
      <AdminEdit
        sess={editSess}
        onBack={() => setEditSess(null)}
        onSaved={() => {
          setEditSess(null);
          loadSessions();
        }}
      />
    );

  const createDemo = async (template) => {
    setCreating(true);
    const sess = {
      code: randomCode(),
      title: template.title,
      status: "lobby",
      rounds: template.rounds.map((r) => ({ ...r, id: uid() })),
      roundIndex: 0,
      roundState: "waiting",
      startedAt: null,
      elapsed: 0,
      participants: [],
      createdAt: Date.now(),
      isDemo: true,
    };
    await S.set(`sess:${sess.code}`, sess);
    await S.set(`ans:${sess.code}`, []);
    await loadSessions();
    setCreating(false);
  };

  const deleteSession = async (code) => {
    const raw = await S.get(`sess:${code}`);
    const sess = normalizeSession(raw);
    if (!sess) return;
    await S.set(`sess:${code}`, {
      ...sess,
      deletedAt: Date.now(),
      status: "deleted",
    });
    setConfirm(null);
    await loadSessions();
  };

  const restoreSession = async (sess) => {
    await S.set(`sess:${sess.code}`, {
      ...sess,
      status: "lobby",
      deletedAt: null,
    });
    await loadSessions();
  };
  const deleteForever = async (code) => {
    await S.del(`sess:${code}`);
    await S.del(`ans:${code}`);
    await loadSessions();
  };
  const restartSession = async (sess) => {
    await S.set(`sess:${sess.code}`, {
      ...sess,
      status: "lobby",
      roundIndex: 0,
      roundState: "waiting",
      startedAt: null,
      elapsed: 0,
      participants: [],
    });
    await S.set(`ans:${sess.code}`, []);
    await loadSessions();
  };
  const downloadAnswers = async (sess) => {
    const raw = await S.get(`ans:${sess.code}`);
    downloadExcel(sess, normalizeArr(raw), lang);
  };

  const STATUS_COLOR = { lobby: C.yellow, active: C.green, finished: C.muted };
  const STATUS_LBL = {
    lobby: t("Lobby", "Lobby"),
    active: t("Actief", "Active"),
    finished: t("Afgerond", "Finished"),
  };

  const activeSessions = sessions.filter((s) => s.status !== "deleted");
  const deletedSessions = sessions.filter((s) => s.status === "deleted");

  return (
    <div
      style={{
        maxWidth: 900,
        margin: "0 auto",
        padding: "44px 24px 80px",
        position: "relative",
        zIndex: 1,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 32,
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            color: DM,
            cursor: "pointer",
            fontSize: 13,
            fontFamily: "'Montserrat',sans-serif",
            fontWeight: 600,
          }}
        >
          ← {t("Terug", "Back")}
        </button>
        <Btn size="sm" ghost color={DM} onClick={() => setLoggedIn(false)}>
          {t("Uitloggen", "Log out")}
        </Btn>
      </div>
      <h2
        style={{
          fontFamily: "'Montserrat',sans-serif",
          fontSize: 32,
          fontWeight: 800,
          marginBottom: 6,
          color: DT,
        }}
      >
        Admin — {t("Sessies", "Sessions")}
      </h2>
      <p
        style={{
          color: DM,
          fontSize: 14,
          marginBottom: 36,
          fontFamily: "'Montserrat',sans-serif",
        }}
      >
        {t(
          "Beheer sessies, bewerk vragen, of maak een demo aan.",
          "Manage sessions, edit questions, or create a demo."
        )}
      </p>

      <Card style={{ marginBottom: 28 }}>
        <div
          style={{
            fontSize: 11,
            color: C.muted,
            textTransform: "uppercase",
            letterSpacing: 1.5,
            marginBottom: 14,
            fontFamily: "'Montserrat',sans-serif",
            fontWeight: 700,
          }}
        >
          ✦ {t("Demo sessies aanmaken", "Create demo sessions")}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {DEMO_SESSIONS.map((d) => (
            <Btn
              key={d.title}
              size="sm"
              color={C.neonblue}
              disabled={creating}
              onClick={() => createDemo(d)}
            >
              + {d.title}
            </Btn>
          ))}
        </div>
      </Card>

      <div
        style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}
      >
        <Btn
          size="sm"
          color={!showTrash ? C.yellow : DM}
          ghost={showTrash}
          onClick={() => setShowTrash(false)}
        >
          📋 {t("Actieve sessies", "Active sessions")} ({activeSessions.length})
        </Btn>
        <Btn
          size="sm"
          color={showTrash ? C.red : DM}
          ghost={!showTrash}
          onClick={() => setShowTrash(true)}
        >
          🗑 {t("Prullenbak", "Trash")} ({deletedSessions.length})
        </Btn>
        <Btn size="sm" ghost color={DM} onClick={loadSessions}>
          ↻ {t("Verversen", "Refresh")}
        </Btn>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spinner />
        </div>
      ) : !showTrash ? (
        activeSessions.length === 0 ? (
          <Card>
            <div
              style={{
                color: C.muted,
                textAlign: "center",
                padding: "24px 0",
                fontFamily: "'Montserrat',sans-serif",
              }}
            >
              {t("Nog geen sessies gevonden.", "No sessions found yet.")}
            </div>
          </Card>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {activeSessions.map((s) => (
              <Card
                key={s.code}
                style={{
                  borderColor:
                    s.status === "active" ? C.green + "60" : C.border,
                }}
              >
                {confirm === s.code ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 10,
                    }}
                  >
                    <span
                      style={{
                        color: C.red,
                        fontWeight: 700,
                        fontSize: 14,
                        fontFamily: "'Montserrat',sans-serif",
                      }}
                    >
                      "{s.title}" {t("naar prullenbak?", "to trash?")}
                    </span>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Btn
                        size="sm"
                        color={C.red}
                        onClick={() => deleteSession(s.code)}
                      >
                        {t("Ja, verwijder", "Yes, delete")}
                      </Btn>
                      <Btn
                        size="sm"
                        ghost
                        color={C.muted}
                        onClick={() => setConfirm(null)}
                      >
                        {t("Annuleer", "Cancel")}
                      </Btn>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: 12 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 5,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: 16,
                            fontFamily: "'Montserrat',sans-serif",
                            color: C.text,
                          }}
                        >
                          {s.title}
                        </span>
                        {s.isDemo && <Badge color={C.neonblue}>Demo</Badge>}
                        <Badge color={STATUS_COLOR[s.status] || C.muted}>
                          {STATUS_LBL[s.status] || s.status}
                        </Badge>
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: C.muted,
                          fontFamily: "'Montserrat',sans-serif",
                        }}
                      >
                        {t("Code:", "Code:")}{" "}
                        <strong style={{ color: C.yellow }}>{s.code}</strong>
                        {" · "}
                        {s.rounds?.length} {t("rondes", "rounds")}
                        {" · "}
                        {s.participants?.length || 0}{" "}
                        {t("deelnemers", "participants")}
                        {s.createdAt &&
                          " · " +
                            new Date(s.createdAt).toLocaleDateString(
                              lang === "nl" ? "nl-NL" : "en-GB"
                            )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <Btn
                        size="sm"
                        color={C.yellow}
                        onClick={() => onOpenSession(s)}
                      >
                        {t("Openen →", "Open →")}
                      </Btn>
                      <Btn
                        size="sm"
                        color={C.neonblue}
                        onClick={() => setEditSess(s)}
                      >
                        ✏ {t("Bewerken", "Edit")}
                      </Btn>
                      {s.status === "finished" && (
                        <>
                          <Btn
                            size="sm"
                            color={C.neonblue}
                            onClick={() => restartSession(s)}
                          >
                            ↺ {t("Opnieuw starten", "Restart")}
                          </Btn>
                          <Btn
                            size="sm"
                            color={C.yellow}
                            onClick={() => downloadAnswers(s)}
                          >
                            📥 Download Excel
                          </Btn>
                        </>
                      )}
                      <Btn
                        size="sm"
                        ghost
                        color={C.red}
                        onClick={() => setConfirm(s.code)}
                      >
                        🗑 {t("Verwijder", "Delete")}
                      </Btn>
                    </div>
                  </>
                )}
              </Card>
            ))}
          </div>
        )
      ) : deletedSessions.length === 0 ? (
        <Card>
          <div
            style={{
              color: C.muted,
              textAlign: "center",
              padding: "24px 0",
              fontFamily: "'Montserrat',sans-serif",
            }}
          >
            {t("Prullenbak is leeg.", "Trash is empty.")}
          </div>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {deletedSessions.map((s) => (
            <Card
              key={s.code}
              style={{ borderColor: C.red + "40", opacity: 0.85 }}
            >
              <div style={{ marginBottom: 12 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 5,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      fontSize: 16,
                      fontFamily: "'Montserrat',sans-serif",
                      color: C.muted,
                    }}
                  >
                    {s.title}
                  </span>
                  {s.isDemo && <Badge color={C.neonblue}>Demo</Badge>}
                  <Badge color={C.red}>{t("Verwijderd", "Deleted")}</Badge>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: C.muted,
                    fontFamily: "'Montserrat',sans-serif",
                  }}
                >
                  {t("Code:", "Code:")}{" "}
                  <strong style={{ color: C.yellow }}>{s.code}</strong>
                  {" · "}
                  {s.rounds?.length} {t("rondes", "rounds")}
                  {s.deletedAt &&
                    ` · ${t("Verwijderd op", "Deleted on")} ` +
                      new Date(s.deletedAt).toLocaleDateString(
                        lang === "nl" ? "nl-NL" : "en-GB"
                      )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <Btn
                  size="sm"
                  color={C.neonblue}
                  onClick={() => restoreSession(s)}
                >
                  ↺ {t("Terugzetten", "Restore")}
                </Btn>
                <Btn
                  size="sm"
                  color={C.yellow}
                  onClick={() => downloadAnswers(s)}
                >
                  📥 Download Excel
                </Btn>
                <Btn
                  size="sm"
                  ghost
                  color={C.red}
                  onClick={() => deleteForever(s.code)}
                >
                  ✕ {t("Definitief verwijderen", "Delete permanently")}
                </Btn>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── RONDE KAART (setup) ──────────────────────────────────────────────
function RoundCard({ round, index, total, onUpdate, onRemove }) {
  const t = useT();
  const TL = useTL();
  return (
    <Card style={{ marginBottom: 12, borderColor: TC[round.type] + "60" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <span
          style={{
            color: TC[round.type],
            fontWeight: 700,
            fontSize: 13,
            fontFamily: "'Montserrat',sans-serif",
          }}
        >
          {TI[round.type]} {t("Ronde", "Round")} {index + 1}
        </span>
        {total > 1 && (
          <button
            onClick={() => onRemove(round.id)}
            style={{
              background: "none",
              border: "none",
              color: C.muted,
              cursor: "pointer",
              fontSize: 12,
              fontFamily: "'Montserrat',sans-serif",
            }}
          >
            ✕ {t("Verwijder", "Remove")}
          </button>
        )}
      </div>
      <div
        style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}
      >
        {["free", "build", "perspective"].map((k) => (
          <button
            key={k}
            onClick={() => onUpdate(round.id, "type", k)}
            style={{
              padding: "4px 12px",
              borderRadius: 100,
              cursor: "pointer",
              border: `1.5px solid ${round.type === k ? TC[k] : C.border}`,
              background: round.type === k ? TC[k] + "25" : "transparent",
              color: round.type === k ? TC[k] : C.muted,
              fontSize: 12,
              fontFamily: "'Montserrat',sans-serif",
              fontWeight: 600,
              transition: "all .12s",
            }}
          >
            {TI[k]} {TL[k]}
          </button>
        ))}
      </div>
      <InputField
        label={t("Rondetitel (optioneel)", "Round title (optional)")}
        value={round.title || ""}
        onChange={(e) => onUpdate(round.id, "title", e.target.value)}
        placeholder={t(
          "Bijv: Eerste ideeën, Verdieping, Klantperspectief…",
          "E.g.: First ideas, Deep dive, Customer perspective…"
        )}
      />
      <InputField
        label={t("Vraag", "Question")}
        multiline
        rows={2}
        value={round.question}
        onChange={(e) => onUpdate(round.id, "question", e.target.value)}
        placeholder={
          round.type === "build"
            ? t(
                "Welk onderwerp bouwen deelnemers op voort?",
                "What topic will participants build on?"
              )
            : round.type === "perspective"
            ? t("Wat is de kernvraag?", "What is the core question?")
            : t("Wat wil je weten?", "What do you want to know?")
        }
      />
      {round.type === "perspective" && (
        <InputField
          label={t("Perspectief-instructie", "Perspective instruction")}
          value={round.hint}
          onChange={(e) => onUpdate(round.id, "hint", e.target.value)}
          placeholder={t(
            "Bijv: Beantwoord dit als klant",
            "E.g.: Answer this as a customer"
          )}
        />
      )}
      <div>
        <div
          style={{
            fontSize: 11,
            color: C.muted,
            textTransform: "uppercase",
            letterSpacing: 1.5,
            marginBottom: 8,
            fontFamily: "'Montserrat',sans-serif",
            fontWeight: 700,
          }}
        >
          {t("Tijdslimiet", "Time limit")}
        </div>
        <div
          style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 8 }}
        >
          {TIME_OPTS.map((o) => {
            const sel = round.seconds === o.v;
            return (
              <button
                key={o.v}
                onClick={() => onUpdate(round.id, "seconds", o.v)}
                style={{
                  padding: "4px 10px",
                  borderRadius: 6,
                  cursor: "pointer",
                  border: `1.5px solid ${sel ? C.yellow : C.border}`,
                  background: sel ? C.yellow + "25" : "transparent",
                  color: sel ? C.yellow : C.muted,
                  fontSize: 11,
                  fontFamily: "'Montserrat',sans-serif",
                  fontWeight: 600,
                  transition: "all .12s",
                }}
              >
                {o.l}
              </button>
            );
          })}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 12,
              color: C.muted,
              fontFamily: "'Montserrat',sans-serif",
            }}
          >
            {t("Of typ seconden (5–300):", "Or type seconds (5–300):")}
          </span>
          <input
            type="number"
            min={5}
            max={300}
            value={round.seconds}
            onChange={(e) =>
              onUpdate(
                round.id,
                "seconds",
                Math.min(300, Math.max(5, parseInt(e.target.value) || 5))
              )
            }
            style={{
              width: 64,
              padding: "4px 8px",
              background: C.input,
              border: `1.5px solid ${C.border}`,
              borderRadius: 6,
              color: "#ffffff",
              fontSize: 12,
              textAlign: "center",
              fontFamily: "'Montserrat',sans-serif",
              outline: "none",
            }}
          />
          <span
            style={{
              fontSize: 12,
              color: C.yellow,
              fontWeight: 700,
              fontFamily: "'Montserrat',sans-serif",
            }}
          >
            = {formatSec(round.seconds)}
          </span>
        </div>
      </div>
    </Card>
  );
}

// ─── TIPS SECTIE (Do's & Don'ts) ─────────────────────────────────────
const SOFT_RED = "#E87070";
function Dot({ color }) {
  return (
    <svg
      width="9"
      height="9"
      viewBox="0 0 9 9"
      style={{ flexShrink: 0, marginTop: 4 }}
    >
      <circle cx="4.5" cy="4.5" r="4.5" fill={color} />
    </svg>
  );
}

function TipsSection() {
  const [open, setOpen] = useState(false);
  const t = useT();
  const dos = [
    t(
      "Begin speels en prikkelend. Metaforen, gevoel of een 'als je een toverstaf had'-vraag werken goed",
      "Start playful and stimulating. Metaphors, feelings or a 'if you had a magic wand' question work well"
    ),
    t(
      "Bouw op van veilig naar scherp, en sluit af met een actiegerichte vraag",
      "Build from safe to sharp, and close with an action-oriented question"
    ),
    t(
      "Gebruik voortbouwen- en perspectief-rondes om ideeën te verdiepen",
      "Use build-on and perspective rounds to deepen ideas"
    ),
    t(
      "Geef elke ronde een duidelijke titel zodat deelnemers weten waar ze zijn",
      "Give each round a clear title so participants know where they are"
    ),
    t(
      "Houd rondes kort. Tijdsdruk stimuleert creativiteit",
      "Keep rounds short. Time pressure stimulates creativity"
    ),
  ];
  const donts = [
    t(
      "Begin niet met zwaar inhoudelijke vragen. Dat remt de verbeelding meteen",
      "Don't start with heavy content questions. That immediately limits imagination"
    ),
    t(
      "Verwijs in latere rondes terug naar eerdere antwoorden, zodat ideeën elkaar versterken en niet los naast elkaar blijven staan",
      "In later rounds, refer back to earlier answers so ideas build on each other rather than standing apart"
    ),
    t(
      "Stop niet te veel in één ronde. Liever één scherpe vraag dan twee vage",
      "Don't put too much in one round. One sharp question is better than two vague ones"
    ),
    t(
      "Laat het ritme niet wegglijden. Wissel af en houd het tempo erin",
      "Don't let the rhythm slip. Vary the pace and keep the tempo up"
    ),
  ];
  return (
    <div style={{ marginBottom: 20 }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          background: `linear-gradient(135deg,${C.surface},${C.input})`,
          border: `2px solid #ffffff30`,
          borderRadius: 12,
          padding: "14px 20px",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontFamily: "'Montserrat',sans-serif",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>🚀</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#ffffff" }}>
            {t("Tips voor een goede sessie", "Tips for a great session")}
          </span>
        </div>
        <span
          style={{
            fontSize: 18,
            color: "#ffffff",
            transition: "transform .2s",
            display: "inline-block",
            transform: open ? "rotate(180deg)" : "none",
          }}
        >
          ▾
        </span>
      </button>
      {open && (
        <div
          style={{
            background: `linear-gradient(135deg,${C.surface},${C.input})`,
            border: `2px solid ${sub}50`,
            borderTop: "none",
            borderRadius: "0 0 12px 12px",
            padding: "20px 20px 24px",
          }}
        >
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <div>
              <div
                style={{
                  fontSize: 12,
                  color: "#22c55e",
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  textTransform: "uppercase",
                  fontFamily: "'Montserrat',sans-serif",
                  marginBottom: 12,
                }}
              >
                Do's
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {dos.map((text, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "flex-start",
                    }}
                  >
                    <Dot color="#22c55e" />
                    <span
                      style={{
                        fontSize: 12,
                        color: C.muted,
                        fontFamily: "'Montserrat',sans-serif",
                        lineHeight: 1.5,
                      }}
                    >
                      {text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div
                style={{
                  fontSize: 12,
                  color: SOFT_RED,
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  textTransform: "uppercase",
                  fontFamily: "'Montserrat',sans-serif",
                  marginBottom: 12,
                }}
              >
                Don'ts
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {donts.map((text, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "flex-start",
                    }}
                  >
                    <Dot color={SOFT_RED} />
                    <span
                      style={{
                        fontSize: 12,
                        color: C.muted,
                        fontFamily: "'Montserrat',sans-serif",
                        lineHeight: 1.5,
                      }}
                    >
                      {text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FACILITATOR: SETUP ───────────────────────────────────────────────
function FSetup({ onBack, onCreate }) {
  const mkR = () => ({
    id: uid(),
    question: "",
    type: "free",
    hint: "",
    seconds: 20,
  });
  const [title, setTitle] = useState("");
  const [rounds, setRounds] = useState([mkR()]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const t = useT();

  const updRound = (id, k, v) =>
    setRounds((r) => r.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const remRound = (id) => setRounds((r) => r.filter((x) => x.id !== id));

  const create = async () => {
    if (!title.trim())
      return setErr(
        t("Geef je sessie een naam.", "Please give your session a name.")
      );
    if (rounds.some((r) => !r.question.trim()))
      return setErr(
        t("Elke ronde heeft een vraag nodig.", "Every round needs a question.")
      );
    setBusy(true);
    const sess = {
      code: randomCode(),
      title: title.trim(),
      status: "lobby",
      rounds,
      roundIndex: 0,
      roundState: "waiting",
      startedAt: null,
      elapsed: 0,
      participants: [],
      createdAt: Date.now(),
    };
    await S.set(`sess:${sess.code}`, sess);
    await S.set(`ans:${sess.code}`, []);
    setBusy(false);
    onCreate(sess.code, sess);
  };

  return (
    <div
      style={{
        maxWidth: 700,
        margin: "0 auto",
        padding: "44px 24px 80px",
        position: "relative",
        zIndex: 1,
      }}
    >
      <button
        onClick={onBack}
        style={{
          background: "none",
          border: "none",
          color: DM,
          cursor: "pointer",
          fontSize: 13,
          fontFamily: "'Montserrat',sans-serif",
          fontWeight: 600,
          marginBottom: 32,
        }}
      >
        ← {t("Terug", "Back")}
      </button>
      <h2
        style={{
          fontFamily: "'Montserrat',sans-serif",
          fontSize: 34,
          fontWeight: 800,
          marginBottom: 6,
          color: DT,
        }}
      >
        {t("Sessie aanmaken", "Create session")}
      </h2>
      <p
        style={{
          color: DM,
          fontSize: 14,
          marginBottom: 24,
          fontFamily: "'Montserrat',sans-serif",
        }}
      >
        {t(
          "Stel rondes in en deel daarna de code met je team.",
          "Set up rounds and then share the code with your team."
        )}
      </p>

      <TipsSection />

      <div style={{ marginTop: 24 }}>
        <InputFieldLight
          label={t("Sessienaam", "Session name")}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t(
            "Bijv: Productbrainstorm Q2",
            "E.g.: Product Brainstorm Q2"
          )}
        />

        <div style={{ marginTop: 28 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 14,
            }}
          >
            <h3
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: DT,
                fontFamily: "'Montserrat',sans-serif",
              }}
            >
              {t("Rondes", "Rounds")} ({rounds.length})
            </h3>
            <button
              onClick={() => setRounds((r) => [...r, mkR()])}
              style={{
                padding: "6px 13px",
                fontSize: 11,
                fontFamily: "'Montserrat',sans-serif",
                fontWeight: 700,
                borderRadius: 8,
                border: "2px solid #ffffff",
                background: "#ffffff",
                color: DT,
                cursor: "pointer",
                transition: "all .15s",
              }}
            >
              + {t("Ronde toevoegen", "Add round")}
            </button>
          </div>
          {rounds.map((r, i) => (
            <RoundCard
              key={r.id}
              round={r}
              index={i}
              total={rounds.length}
              onUpdate={updRound}
              onRemove={remRound}
            />
          ))}
        </div>

        {err && (
          <p
            style={{
              color: C.red,
              fontSize: 13,
              marginTop: 8,
              fontWeight: 700,
              fontFamily: "'Montserrat',sans-serif",
            }}
          >
            {err}
          </p>
        )}

        {rounds.length > 0 &&
          (() => {
            const totalSec = rounds.reduce(
              (sum, r) => sum + (r.seconds || 20),
              0
            );
            const totalMin = Math.ceil(totalSec / 60);
            return (
              <div
                style={{
                  marginTop: 20,
                  padding: "14px 20px",
                  background: `linear-gradient(135deg,${C.surface},${C.input})`,
                  border: `2px solid #ffffff20`,
                  borderRadius: 12,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <span style={{ fontSize: 15 }}>⏱</span>
                    <span
                      style={{
                        fontSize: 13,
                        color: "#ffffff",
                        fontFamily: "'Montserrat',sans-serif",
                        fontWeight: 600,
                      }}
                    >
                      {t("Totale schrijftijd", "Total writing time")}
                    </span>
                  </div>
                  <div
                    style={{ display: "flex", alignItems: "baseline", gap: 6 }}
                  >
                    {rounds.map((r, i) => (
                      <span
                        key={r.id}
                        style={{
                          fontSize: 12,
                          color: "#ffffff",
                          fontFamily: "'Montserrat',sans-serif",
                          fontWeight: 700,
                        }}
                      >
                        {i > 0 && (
                          <span style={{ color: C.muted, margin: "0 2px" }}>
                            +
                          </span>
                        )}
                        {formatSec(r.seconds)}
                      </span>
                    ))}
                    <span
                      style={{ color: C.muted, margin: "0 6px", fontSize: 12 }}
                    >
                      =
                    </span>
                    <span
                      style={{
                        fontFamily: "'Montserrat',sans-serif",
                        fontSize: 20,
                        fontWeight: 800,
                        color: "#ffffff",
                      }}
                    >
                      {totalMin} min
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}

        <div style={{ marginTop: 24 }}>
          <button
            disabled={busy}
            onClick={create}
            style={{
              width: "100%",
              padding: "14px 32px",
              fontSize: 15,
              fontFamily: "'Montserrat',sans-serif",
              fontWeight: 700,
              borderRadius: 8,
              border: `2px solid ${accent}`,
              background: accent,
              color: "#ffffff",
              opacity: busy ? 0.4 : 1,
              cursor: busy ? "not-allowed" : "pointer",
              transition: "all .15s",
            }}
            onMouseEnter={(e) => {
              if (!busy) {
                e.currentTarget.style.background = accent + "cc";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = accent;
            }}
          >
            {busy
              ? t("Aanmaken…", "Creating…")
              : t("Sessie aanmaken →", "Create session →")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── FACILITATOR: LOBBY ───────────────────────────────────────────────
function FLobby({ sess, setSess, code, onEnd, onBack }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const startSession = async () => {
    const upd = { ...sess, status: "active", roundState: "waiting" };
    await S.set(`sess:${code}`, upd);
    setSess(upd);
  };
  const endSession = async () => {
    const upd = { ...sess, status: "finished", roundState: "done" };
    await S.set(`sess:${code}`, upd);
    setSess(upd);
    onEnd();
  };

  if (editing)
    return (
      <AdminEdit
        sess={sess}
        onBack={() => setEditing(false)}
        onSaved={(updated) => {
          setSess(updated);
          setEditing(false);
        }}
      />
    );

  const nPart = sess.participants?.length ?? 0;
  const joinLink = `https://bwpink.thinktankcollective.nl?code=${sess.code}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(
    joinLink
  )}&color=C724AE&bgcolor=F2A7D8`;
  const totalSec =
    sess.rounds?.reduce((sum, r) => sum + (r.seconds || 20), 0) ?? 0;
  const totalMin = Math.ceil(totalSec / 60);

  return (
    <div
      style={{
        maxWidth: 620,
        margin: "0 auto",
        padding: "32px 24px 52px",
        textAlign: "center",
        position: "relative",
        zIndex: 1,
      }}
    >
      <Clouds />
      <div style={{ position: "relative", zIndex: 1 }}>
        <div style={{ textAlign: "left", marginBottom: 20 }}>
          <button
            onClick={onBack}
            style={{
              background: "none",
              border: "none",
              color: DT,
              cursor: "pointer",
              fontSize: 13,
              fontFamily: "'Montserrat',sans-serif",
              fontWeight: 600,
            }}
          >
            ← {t("Terug naar home", "Back to home")}
          </button>
        </div>
        <div
          style={{
            fontSize: 13,
            color: C.yellow,
            letterSpacing: 4,
            textTransform: "uppercase",
            marginBottom: 16,
            fontWeight: 700,
            fontFamily: "'Montserrat',sans-serif",
            textShadow: `0 0 12px ${C.yellow}80`,
          }}
        >
          {t("Sessie gereed", "Session ready")}
        </div>
        <h2
          style={{
            fontFamily: "'Montserrat',sans-serif",
            fontSize: 28,
            fontWeight: 800,
            marginBottom: 8,
            color: C.text,
          }}
        >
          {sess.title}
        </h2>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 12,
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          <span
            style={{
              fontSize: 13,
              color: C.neonblue,
              fontFamily: "'Montserrat',sans-serif",
              fontWeight: 700,
            }}
          >
            ⏱ ~{totalMin} min · {sess.rounds?.length} {t("rondes", "rounds")}
          </span>
        </div>
        <div style={{ marginBottom: 20 }}>
          <Btn size="sm" ghost color={C.red} onClick={() => setEditing(true)}>
            ✏ {t("Sessie bewerken", "Edit session")}
          </Btn>
        </div>

        <div
          style={{
            background: `linear-gradient(135deg,${C.surface},${C.input})`,
            border: `4px solid ${C.yellow}`,
            borderRadius: 18,
            padding: "32px 44px",
            marginBottom: 24,
            boxShadow: `0 0 0 6px ${C.surface}, 0 8px 40px #00000040`,
          }}
        >
          <div
            style={{
              fontSize: 11,
              color: C.muted,
              letterSpacing: 3,
              textTransform: "uppercase",
              marginBottom: 12,
              fontFamily: "'Montserrat',sans-serif",
              fontWeight: 700,
            }}
          >
            {t(
              "Sessiecode — deel dit met je team",
              "Session code — share with your team"
            )}
          </div>
          <div
            style={{
              fontFamily: "'Montserrat',sans-serif",
              fontSize: 72,
              fontWeight: 800,
              color: C.yellow,
              letterSpacing: 12,
              lineHeight: 1,
              textShadow: `0 0 30px ${C.yellow}80, 0 4px 0px #8B6000`,
            }}
          >
            {code}
          </div>
          <div
            style={{
              marginTop: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 20,
              flexWrap: "wrap",
            }}
          >
            <Btn size="sm" ghost color={C.yellow} onClick={copy}>
              {copied
                ? `✓ ${t("Gekopieerd!", "Copied!")}`
                : t("Kopieer code", "Copy code")}
            </Btn>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
              }}
            >
              <img
                src={qrUrl}
                alt="QR code"
                style={{
                  width: 90,
                  height: 90,
                  borderRadius: 8,
                  border: `2px solid ${C.yellow}60`,
                }}
              />
              <span
                style={{
                  fontSize: 10,
                  color: C.muted,
                  fontFamily: "'Montserrat',sans-serif",
                  letterSpacing: 1,
                }}
              >
                {t("SCAN OM TE OPENEN", "SCAN TO OPEN")}
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            background: "#FFD70022",
            border: `2px solid #FFD700`,
            borderRadius: 12,
            padding: "16px 20px",
            marginBottom: 20,
            textAlign: "left",
          }}
        >
          <span style={{ fontSize: 20, flexShrink: 0 }}>⚠️</span>
          <span
            style={{
              fontSize: 13,
              color: "#ffffff",
              fontFamily: "'Montserrat',sans-serif",
              lineHeight: 1.7,
              fontWeight: 500,
            }}
          >
            <strong style={{ color: C.yellow, fontWeight: 800 }}>
              {t("Sla deze code op.", "Save this code.")}
            </strong>{" "}
            {t(
              "De code is uniek en heb je nodig om straks bij jouw voorbereide sessie te komen. Ben je de code kwijt? Dan zul je een nieuwe sessie moeten aanmaken.",
              "The code is unique and you'll need it to return to your session. Lost your code? You'll need to create a new session."
            )}
          </span>
        </div>

        <div
          style={{
            background: `linear-gradient(135deg,${C.surface},#1a3a7a)`,
            border: `2px solid ${sub}`,
            borderRadius: 14,
            padding: "20px 24px",
            fontSize: 13,
            color: C.text,
            lineHeight: 2,
            marginBottom: 20,
            textAlign: "left",
            fontFamily: "'Montserrat',sans-serif",
            boxShadow: `0 0 20px ${sub}70, inset 0 0 30px ${sub}10`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 10,
            }}
          >
            <span style={{ fontSize: 18 }}>🚀</span>
            <strong style={{ color: C.yellow, fontSize: 14, letterSpacing: 1 }}>
              {t("Zo loggen deelnemers in:", "How participants join:")}
            </strong>
          </div>
          1. {t("Open een", "Open a")}{" "}
          <strong style={{ color: C.neonblue }}>
            {t("nieuw tabblad", "new tab")}
          </strong>
          <br />
          2. {t("Ga naar", "Go to")}{" "}
          <strong style={{ color: C.neonblue }}>
            bwpink.thinktankcollective.nl?code={sess.code}
          </strong>
          <br />
          3. {t("Klik op", "Click")}{" "}
          <strong style={{ color: C.neonblue }}>
            "{t("Deelnemen", "Join")}"
          </strong>
          <br />
          4. {t("Voer code", "Enter code")}{" "}
          <strong style={{ color: C.yellow, fontSize: 15 }}>{code}</strong>{" "}
          {t("in + naam", "+ name")}
        </div>

        <div
          style={{
            background: `linear-gradient(135deg,${C.surface},#0f3d1a)`,
            border: `2px solid ${C.green}`,
            borderRadius: 14,
            padding: "20px 24px",
            marginBottom: 24,
            textAlign: "left",
            boxShadow: `0 0 20px ${C.green}50, inset 0 0 30px ${C.green}08`,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 14,
            }}
          >
            <span style={{ fontSize: 18 }}>👽</span>
            <div
              style={{
                fontSize: 11,
                color: C.neonblue,
                textTransform: "uppercase",
                letterSpacing: 1.5,
                fontFamily: "'Montserrat',sans-serif",
                fontWeight: 700,
              }}
            >
              {t("Ingelogde deelnemers", "Logged in participants")} ({nPart})
            </div>
          </div>
          {nPart === 0 ? (
            <div
              style={{
                color: C.muted,
                fontSize: 14,
                padding: "8px 0",
                fontFamily: "'Montserrat',sans-serif",
              }}
            >
              {t(
                "Nog niemand ingelogd. Wachten…",
                "No one logged in yet. Waiting…"
              )}
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))",
                gap: 8,
              }}
            >
              {sess.participants.map((p) => (
                <div
                  key={p.id}
                  style={{
                    background: C.green + "20",
                    border: `1.5px solid ${C.green}60`,
                    borderRadius: 8,
                    padding: "8px 12px",
                    fontSize: 14,
                    color: C.neonblue,
                    fontWeight: 700,
                    fontFamily: "'Montserrat',sans-serif",
                  }}
                >
                  ✓ {p.name}
                </div>
              ))}
            </div>
          )}
        </div>

        <Btn
          size="lg"
          full
          disabled={nPart === 0}
          color={nPart > 0 ? C.pink : C.surface}
          onClick={startSession}
        >
          {nPart === 0
            ? `⏳ ${t("Wachten op deelnemers…", "Waiting for participants…")}`
            : `▶ ${t("Sessie starten met", "Start session with")} ${nPart} ${
                nPart !== 1
                  ? t("deelnemers", "participants")
                  : t("deelnemer", "participant")
              }`}
        </Btn>
        {nPart > 0 && !confirmCancel && (
          <div style={{ marginTop: 10 }}>
            <Btn
              full
              ghost
              color={C.red}
              onClick={() => setConfirmCancel(true)}
            >
              ✕ {t("Sessie annuleren", "Cancel session")}
            </Btn>
          </div>
        )}
        {confirmCancel && (
          <div
            style={{
              marginTop: 12,
              background: C.red + "18",
              border: `2px solid ${C.red}60`,
              borderRadius: 12,
              padding: "16px 20px",
              textAlign: "center",
            }}
          >
            <p
              style={{
                color: C.red,
                fontWeight: 700,
                fontSize: 14,
                fontFamily: "'Montserrat',sans-serif",
                marginBottom: 12,
              }}
            >
              ⚠{" "}
              {t(
                "Weet je het zeker? De sessie wordt verwijderd.",
                "Are you sure? The session will be deleted."
              )}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <Btn color={C.red} onClick={endSession}>
                {t("Ja, annuleer sessie", "Yes, cancel session")}
              </Btn>
              <Btn
                ghost
                color={C.muted}
                onClick={() => setConfirmCancel(false)}
              >
                {t("Nee, ga terug", "No, go back")}
              </Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── FACILITATOR: LIVE ────────────────────────────────────────────────
function FLive({ sess, setSess, answers, code, onEnd }) {
  const t = useT();
  const TL = useTL();
  const cur = sess.rounds[sess.roundIndex];
  const curAns = answers.filter(
    (a) => Number(a.roundIndex) === sess.roundIndex
  );
  const isLast = sess.roundIndex === sess.rounds.length - 1;
  const save = async (upd) => {
    await S.set(`sess:${code}`, upd);
    setSess(upd);
  };
  const [confirmEnd, setConfirmEnd] = useState(false);
  const endSessionNow = async () => {
    const upd = { ...sess, status: "finished", roundState: "done" };
    await S.set(`sess:${code}`, upd);
    setSess(upd);
    onEnd();
  };

  return (
    <div
      style={{
        maxWidth: 1100,
        margin: "0 auto",
        padding: "28px 20px",
        position: "relative",
        zIndex: 1,
      }}
    >
      <Clouds />
      <Ball
        size={38}
        color={C.yellow}
        highlight="#fff0a0"
        style={{ top: 70, right: 50 }}
      />
      <Ball
        size={46}
        color="#7B4FBE"
        highlight="#c49be8"
        style={{ bottom: 60, left: 40 }}
      />
      <Ball
        size={28}
        color={C.green}
        highlight="#7ddd7d"
        style={{ bottom: 140, right: 30 }}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 22,
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 10,
              color: C.yellow,
              letterSpacing: 3,
              textTransform: "uppercase",
              fontWeight: 700,
              fontFamily: "'Montserrat',sans-serif",
            }}
          >
            {t("Facilitator · Live", "Facilitator · Live")}
          </div>
          <h2
            style={{
              fontFamily: "'Montserrat',sans-serif",
              fontSize: 26,
              fontWeight: 800,
              marginTop: 2,
              color: C.text,
            }}
          >
            {sess.title}
          </h2>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexWrap: "wrap",
          }}
        >
          <Card
            style={{
              padding: "8px 20px",
              textAlign: "center",
              borderColor: C.yellow + "50",
            }}
          >
            <div
              style={{
                fontSize: 9,
                color: C.muted,
                letterSpacing: 2,
                textTransform: "uppercase",
                fontFamily: "'Montserrat',sans-serif",
                fontWeight: 700,
              }}
            >
              {t("Code", "Code")}
            </div>
            <div
              style={{
                fontFamily: "'Montserrat',sans-serif",
                fontSize: 28,
                fontWeight: 800,
                color: C.yellow,
                letterSpacing: 6,
              }}
            >
              {code}
            </div>
          </Card>
          <Btn
            ghost
            color={C.red}
            size="sm"
            onClick={() => setConfirmEnd(true)}
          >
            ✕ {t("Beëindig sessie", "End session")}
          </Btn>
          {confirmEnd && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "#00000060",
                zIndex: 100,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
              }}
            >
              <div
                style={{
                  background: C.surface,
                  border: `2px solid ${C.red}`,
                  borderRadius: 16,
                  padding: "28px 32px",
                  maxWidth: 380,
                  textAlign: "center",
                  boxShadow: `0 0 40px #00000060`,
                }}
              >
                <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
                <h3
                  style={{
                    fontFamily: "'Montserrat',sans-serif",
                    fontSize: 18,
                    fontWeight: 800,
                    color: "#ffffff",
                    marginBottom: 8,
                  }}
                >
                  {t("Sessie beëindigen?", "End session?")}
                </h3>
                <p
                  style={{
                    color: C.muted,
                    fontSize: 13,
                    fontFamily: "'Montserrat',sans-serif",
                    marginBottom: 20,
                  }}
                >
                  {t(
                    "Alle deelnemers worden uitgelogd en de sessie wordt afgesloten.",
                    "All participants will be logged out and the session will be closed."
                  )}
                </p>
                <div
                  style={{ display: "flex", gap: 10, justifyContent: "center" }}
                >
                  <Btn color={C.red} onClick={endSessionNow}>
                    {t("Ja, beëindig", "Yes, end")}
                  </Btn>
                  <Btn
                    ghost
                    color={C.muted}
                    onClick={() => setConfirmEnd(false)}
                  >
                    {t("Annuleer", "Cancel")}
                  </Btn>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <ProgressBar current={sess.roundIndex} total={sess.rounds.length} />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(250px,290px) 1fr",
          gap: 16,
          alignItems: "start",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Card>
            <Badge color={TC[cur.type]}>
              {TI[cur.type]} {TL[cur.type]}
            </Badge>
            {cur.title && (
              <p
                style={{
                  fontFamily: "'Montserrat',sans-serif",
                  fontSize: 13,
                  fontWeight: 700,
                  color: sub,
                  marginTop: 8,
                  letterSpacing: 0.3,
                }}
              >
                {cur.title}
              </p>
            )}
            <p
              style={{
                fontFamily: "'Montserrat',sans-serif",
                fontSize: 16,
                fontWeight: 600,
                lineHeight: 1.5,
                marginTop: 6,
                color: C.text,
              }}
            >
              {cur.question}
            </p>
            {cur.type === "perspective" && cur.hint && (
              <div
                style={{
                  marginTop: 8,
                  padding: "8px 12px",
                  background: C.green + "20",
                  borderRadius: 6,
                  color: C.neonblue,
                  fontSize: 13,
                  border: `1px solid ${C.green}40`,
                  fontFamily: "'Montserrat',sans-serif",
                }}
              >
                ◎ {cur.hint}
              </div>
            )}
            <div
              style={{
                marginTop: 18,
                paddingTop: 16,
                borderTop: `1px solid ${C.border}`,
              }}
            >
              <Clock sess={sess} />
            </div>
            <div
              style={{
                marginTop: 16,
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              {sess.roundState === "waiting" && (
                <Btn
                  full
                  color={C.yellow}
                  onClick={() =>
                    save({
                      ...sess,
                      roundState: "running",
                      startedAt: Date.now(),
                      elapsed: 0,
                    })
                  }
                >
                  ▶ {t("Start ronde", "Start round")} {sess.roundIndex + 1}
                </Btn>
              )}
              {sess.roundState === "running" && (
                <>
                  <Btn
                    full
                    ghost
                    color={C.neonblue}
                    onClick={() =>
                      save({
                        ...sess,
                        roundState: "paused",
                        elapsed:
                          (sess.elapsed || 0) +
                          (Date.now() - sess.startedAt) / 1000,
                      })
                    }
                  >
                    ⏸ {t("Pauzeer", "Pause")}
                  </Btn>
                  <Btn
                    full
                    ghost
                    color={C.red}
                    onClick={() => save({ ...sess, roundState: "done" })}
                  >
                    ■ {t("Beëindig ronde", "End round")}
                  </Btn>
                </>
              )}
              {sess.roundState === "paused" && (
                <>
                  <Btn
                    full
                    color={C.yellow}
                    onClick={() =>
                      save({
                        ...sess,
                        roundState: "running",
                        startedAt: Date.now(),
                      })
                    }
                  >
                    ▶ {t("Hervat", "Resume")}
                  </Btn>
                  <Btn
                    full
                    ghost
                    color={C.red}
                    onClick={() => save({ ...sess, roundState: "done" })}
                  >
                    ■ {t("Beëindig ronde", "End round")}
                  </Btn>
                </>
              )}
              {sess.roundState === "done" && (
                <>
                  <div
                    style={{
                      textAlign: "center",
                      color: C.neonblue,
                      fontSize: 13,
                      fontWeight: 700,
                      fontFamily: "'Montserrat',sans-serif",
                    }}
                  >
                    ✓ {t("Ronde afgerond", "Round finished")}
                  </div>
                  <Btn
                    full
                    color={C.yellow}
                    onClick={() => {
                      const n = sess.roundIndex + 1;
                      n >= sess.rounds.length
                        ? save({
                            ...sess,
                            status: "finished",
                            roundState: "done",
                          })
                        : save({
                            ...sess,
                            roundIndex: n,
                            roundState: "waiting",
                            startedAt: null,
                            elapsed: 0,
                          });
                    }}
                  >
                    {isLast
                      ? t("Sessie afsluiten →", "Close session →")
                      : t("Volgende ronde →", "Next round →")}
                  </Btn>
                </>
              )}
            </div>
          </Card>
          <Card>
            <div
              style={{
                fontSize: 11,
                color: C.muted,
                textTransform: "uppercase",
                letterSpacing: 1.5,
                marginBottom: 10,
                fontFamily: "'Montserrat',sans-serif",
                fontWeight: 700,
              }}
            >
              {t("Deelnemers", "Participants")} ({sess.participants.length})
            </div>
            {sess.participants.map((p) => (
              <div
                key={p.id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "6px 0",
                  borderBottom: `1px solid ${C.border}`,
                }}
              >
                <span
                  style={{
                    fontSize: 14,
                    color: C.text,
                    fontFamily: "'Montserrat',sans-serif",
                  }}
                >
                  {p.name}
                </span>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    fontFamily: "'Montserrat',sans-serif",
                    color: curAns.some((a) => a.participantId === p.id)
                      ? C.green
                      : C.muted,
                  }}
                >
                  {curAns.some((a) => a.participantId === p.id)
                    ? `✓ ${t("ingediend", "submitted")}`
                    : "—"}
                </span>
              </div>
            ))}
          </Card>
        </div>

        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <span
              style={{
                fontSize: 11,
                color: C.muted,
                textTransform: "uppercase",
                letterSpacing: 1.5,
                fontFamily: "'Montserrat',sans-serif",
                fontWeight: 700,
              }}
            >
              {t("Live antwoorden", "Live answers")}
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                fontFamily: "'Montserrat',sans-serif",
                color:
                  curAns.length === sess.participants.length &&
                  sess.participants.length > 0
                    ? C.green
                    : C.muted,
              }}
            >
              {curAns.length} / {sess.participants.length}
            </span>
          </div>
          {curAns.length === 0 ? (
            <Card>
              <div
                style={{
                  color: C.muted,
                  textAlign: "center",
                  padding: "28px 0",
                  fontSize: 14,
                  fontFamily: "'Montserrat',sans-serif",
                }}
              >
                {sess.roundState === "waiting"
                  ? t(
                      "Start de ronde om antwoorden te ontvangen.",
                      "Start the round to receive answers."
                    )
                  : t("Wachten op antwoorden…", "Waiting for answers…")}
              </div>
            </Card>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))",
                gap: 10,
              }}
            >
              {curAns.map((a) => (
                <Card
                  key={a.id}
                  style={{
                    borderColor: TC[cur.type] + "60",
                    background: C.input,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: TC[cur.type],
                      marginBottom: 8,
                      fontWeight: 700,
                      fontFamily: "'Montserrat',sans-serif",
                    }}
                  >
                    {a.participantName}
                  </div>
                  <p
                    style={{
                      fontSize: 14,
                      lineHeight: 1.65,
                      color: "#ffffff",
                      fontFamily: "'Montserrat',sans-serif",
                    }}
                  >
                    {a.answer}
                  </p>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── RESULTATEN ───────────────────────────────────────────────────────
function Results({ sess, answers: initialAnswers, onReset }) {
  const t = useT();
  const TL = useTL();
  const lang = useLang();
  const [answers, setAnswers] = useState(initialAnswers);

  useEffect(() => {
    const load = async () => {
      const raw = await S.get(`ans:${sess.code}`);
      if (raw) setAnswers(normalizeArr(raw));
    };
    load();
  }, [sess.code]);

  const [delConfirm, setDelConfirm] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const deleteData = async () => {
    // Verwijder antwoorden
    await S.del(`ans:${sess.code}`);
    // Verwijder sessie-inhoud maar bewaar een anonieme gebruikslog voor backend
    await S.set(`${SECRET}/usage_log/${sess.code}`, {
      deletedAt: Date.now(),
      rounds: sess.rounds.length,
      participants: sess.participants.length,
      title: "[verwijderd]",
    });
    setAnswers([]);
    setDeleted(true);
    setDelConfirm(false);
  };

  return (
    <div
      style={{
        maxWidth: 900,
        margin: "0 auto",
        padding: "52px 24px",
        position: "relative",
        zIndex: 1,
      }}
    >
      <Clouds />
      <Ball
        size={52}
        color={C.yellow}
        highlight="#fff0a0"
        style={{ top: 60, right: 60 }}
      />
      <Ball
        size={62}
        color="#7B4FBE"
        highlight="#c49be8"
        style={{ bottom: 80, left: 40 }}
      />
      <Ball
        size={38}
        color={C.green}
        highlight="#7ddd7d"
        style={{ top: 140, left: 100 }}
      />
      <Ball
        size={46}
        color={C.red}
        highlight="#ff8c8c"
        style={{ bottom: 160, right: 120 }}
      />
      <Ball
        size={24}
        color={C.pink}
        highlight="#ffb8d4"
        style={{ top: 200, right: 180 }}
      />

      <div
        style={{
          textAlign: "center",
          marginBottom: 48,
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ fontSize: 56, marginBottom: 8 }}>🎉</div>
        <h2
          style={{
            fontFamily: "'Montserrat',sans-serif",
            fontSize: 38,
            fontWeight: 800,
            color: DT,
            textShadow: `0 0 20px ${C.surface}30`,
          }}
        >
          {t("Sessie afgerond", "Session finished")}
        </h2>
        <p
          style={{
            color: sub,
            marginTop: 8,
            fontSize: 15,
            fontFamily: "'Montserrat',sans-serif",
            fontWeight: 600,
          }}
        >
          {sess.title} · {sess.rounds.length} {t("rondes", "rounds")} ·{" "}
          {sess.participants.length} {t("deelnemers", "participants")}
        </p>
        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "center",
            marginTop: 24,
            flexWrap: "wrap",
          }}
        >
          <Btn
            size="lg"
            color={C.neonblue}
            onClick={() => downloadExcel(sess, answers, lang)}
          >
            📥 Download Excel
          </Btn>
          <Btn ghost color={C.pink} onClick={onReset}>
            ← {t("Nieuwe sessie", "New session")}
          </Btn>
          {!deleted && (
            <Btn
              ghost
              color={C.red}
              size="sm"
              onClick={() => setDelConfirm(true)}
            >
              🗑 {t("Verwijder data", "Delete data")}
            </Btn>
          )}
          {deleted && (
            <span
              style={{
                fontSize: 13,
                color: C.green,
                fontWeight: 700,
                fontFamily: "'Montserrat',sans-serif",
                alignSelf: "center",
              }}
            >
              ✓ {t("Data verwijderd", "Data deleted")}
            </span>
          )}
        </div>

        {/* Bevestiging dialog */}
        {delConfirm && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "#00000060",
              zIndex: 100,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
            }}
          >
            <div
              style={{
                background: C.surface,
                border: `2px solid ${C.red}`,
                borderRadius: 16,
                padding: "28px 32px",
                maxWidth: 400,
                textAlign: "center",
                boxShadow: `0 0 40px #00000060`,
              }}
            >
              <div style={{ fontSize: 36, marginBottom: 12 }}>🗑</div>
              <h3
                style={{
                  fontFamily: "'Montserrat',sans-serif",
                  fontSize: 18,
                  fontWeight: 800,
                  color: "#ffffff",
                  marginBottom: 8,
                }}
              >
                {t("Data verwijderen?", "Delete data?")}
              </h3>
              <p
                style={{
                  color: C.muted,
                  fontSize: 13,
                  fontFamily: "'Montserrat',sans-serif",
                  marginBottom: 8,
                  lineHeight: 1.6,
                }}
              >
                {t(
                  "Alle antwoorden van deze sessie worden permanent verwijderd.",
                  "All answers from this session will be permanently deleted."
                )}
              </p>
              <p
                style={{
                  color: C.neonblue,
                  fontSize: 12,
                  fontFamily: "'Montserrat',sans-serif",
                  marginBottom: 20,
                  lineHeight: 1.5,
                }}
              >
                💡{" "}
                {t(
                  "Tip: download eerst de Excel als je de data wilt bewaren.",
                  "Tip: download the Excel first if you want to keep the data."
                )}
              </p>
              <div
                style={{ display: "flex", gap: 10, justifyContent: "center" }}
              >
                <Btn color={C.red} onClick={deleteData}>
                  {t("Ja, verwijder", "Yes, delete")}
                </Btn>
                <Btn ghost color={C.muted} onClick={() => setDelConfirm(false)}>
                  {t("Annuleer", "Cancel")}
                </Btn>
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ position: "relative", zIndex: 1 }}>
        {sess.rounds.map((r, i) => {
          const ra = answers.filter((a) => Number(a.roundIndex) === i);
          return (
            <div key={r.id} style={{ marginBottom: 36 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                  flexWrap: "wrap",
                }}
              >
                <Badge color={sub}>
                  {TI[r.type]} {t("Ronde", "Round")} {i + 1}
                </Badge>
                <span
                  style={{
                    color: DT,
                    fontSize: 15,
                    fontFamily: "'Montserrat',sans-serif",
                    fontWeight: 700,
                  }}
                >
                  {r.question}
                </span>
                <span
                  style={{
                    color: DM,
                    fontSize: 12,
                    fontFamily: "'Montserrat',sans-serif",
                  }}
                >
                  ({formatSec(r.seconds)})
                </span>
              </div>
              {ra.length === 0 ? (
                <p
                  style={{
                    color: C.muted,
                    fontSize: 14,
                    fontFamily: "'Montserrat',sans-serif",
                  }}
                >
                  {t("Geen antwoorden ontvangen.", "No answers received.")}
                </p>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
                    gap: 8,
                  }}
                >
                  {ra.map((a) => (
                    <Card
                      key={a.id}
                      style={{
                        borderColor: TC[r.type] + "50",
                        background: C.input,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 11,
                          color: TC[r.type],
                          marginBottom: 6,
                          fontWeight: 700,
                          fontFamily: "'Montserrat',sans-serif",
                        }}
                      >
                        {a.participantName}
                      </div>
                      <p
                        style={{
                          fontSize: 14,
                          lineHeight: 1.65,
                          color: "#ffffff",
                          fontFamily: "'Montserrat',sans-serif",
                        }}
                      >
                        {a.answer}
                      </p>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── FACILITATOR: HERVATTEN ───────────────────────────────────────────
function FResume({ onBack, onFound }) {
  const t = useT();
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const find = async () => {
    if (!code.trim())
      return setErr(
        t("Voer je sessiecode in.", "Please enter your session code.")
      );
    setBusy(true);
    setErr("");
    const zoekCode = code.toUpperCase().trim();
    const raw = await S.get(`sess:${zoekCode}`);
    const sess = normalizeSession(raw);
    if (!sess) {
      setBusy(false);
      return setErr(
        `${t("Sessie", "Session")} '${zoekCode}' ${t(
          "niet gevonden.",
          "not found."
        )}`
      );
    }
    setBusy(false);
    onFound(sess);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: `radial-gradient(ellipse 60% 50% at 50% 50%,${C.pink}20,transparent),${C.bg}`,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          position: "relative",
          zIndex: 1,
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            color: DM,
            cursor: "pointer",
            fontSize: 13,
            fontFamily: "'Montserrat',sans-serif",
            fontWeight: 600,
            marginBottom: 36,
          }}
        >
          ← {t("Terug", "Back")}
        </button>
        <div style={{ fontSize: 42, marginBottom: 16, textAlign: "center" }}>
          🔑
        </div>
        <h2
          style={{
            fontFamily: "'Montserrat',sans-serif",
            fontSize: 32,
            fontWeight: 800,
            marginBottom: 6,
            color: DT,
            textAlign: "center",
          }}
        >
          {t("Sessie hervatten", "Resume session")}
        </h2>
        <p
          style={{
            color: DM,
            fontSize: 14,
            marginBottom: 28,
            fontFamily: "'Montserrat',sans-serif",
            textAlign: "center",
          }}
        >
          {t(
            "Voer de code in van je eerder aangemaakte sessie.",
            "Enter the code of your previously created session."
          )}
        </p>
        <InputFieldLight
          label={t("Jouw sessiecode", "Your session code")}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder={t("Bijv: K7X2QP", "E.g.: K7X2QP")}
          mono
        />
        {err && (
          <div
            style={{
              background: C.red + "20",
              border: `1px solid ${C.red}50`,
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 14,
            }}
          >
            <p
              style={{
                color: C.red,
                fontSize: 13,
                fontWeight: 700,
                fontFamily: "'Montserrat',sans-serif",
              }}
            >
              {err}
            </p>
          </div>
        )}
        <Btn size="lg" full color={C.pink} disabled={busy} onClick={find}>
          {busy
            ? t("Zoeken…", "Searching…")
            : t("Sessie ophalen →", "Retrieve session →")}
        </Btn>
        <div
          style={{
            marginTop: 20,
            padding: "14px 18px",
            background: C.yellow + "40",
            border: `2px solid ${C.yellow}`,
            borderRadius: 10,
          }}
        >
          <p
            style={{
              color: DT,
              fontSize: 12,
              fontFamily: "'Montserrat',sans-serif",
              fontWeight: 700,
            }}
          >
            💡{" "}
            {t(
              "Tip: bewaar je sessiecode altijd na het aanmaken — je hebt hem nodig om terug te komen.",
              "Tip: always save your session code after creating it — you'll need it to return."
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── DEELNEMER: INLOGGEN ──────────────────────────────────────────────
function Join({ onBack, onJoined, initialCode = "" }) {
  const t = useT();
  const [code, setCode] = useState(initialCode);
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const join = async () => {
    if (!code.trim())
      return setErr(
        t("Voer een sessiecode in.", "Please enter a session code.")
      );
    if (!name.trim())
      return setErr(t("Voer je naam in.", "Please enter your name."));
    setBusy(true);
    setErr("");
    const zoekCode = code.toUpperCase().trim();
    const raw = await S.get(`sess:${zoekCode}`);
    const sess = normalizeSession(raw);
    if (!sess) {
      setBusy(false);
      return setErr(
        `${t("Sessie", "Session")} '${zoekCode}' ${t(
          "niet gevonden.",
          "not found."
        )}`
      );
    }
    if (sess.status === "finished") {
      setBusy(false);
      return setErr(
        t("Sessie is al afgelopen.", "This session has already ended.")
      );
    }
    const myId = uid();
    const me = { id: myId, name: name.trim(), joinedAt: Date.now() };
    const upd = {
      ...sess,
      participants: [
        ...sess.participants.filter((p) => p.name !== me.name),
        me,
      ],
    };
    await S.set(`sess:${upd.code}`, upd);
    setBusy(false);
    onJoined(upd.code, myId, me.name);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: `radial-gradient(ellipse 60% 50% at 50% 50%,${accent}12,transparent),${C.bg}`,
        position: "relative",
      }}
    >
      <Clouds />
      <div
        style={{
          width: "100%",
          maxWidth: 440,
          position: "relative",
          zIndex: 1,
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            color: DT,
            cursor: "pointer",
            fontSize: 13,
            fontFamily: "'Montserrat',sans-serif",
            fontWeight: 600,
            marginBottom: 36,
          }}
        >
          ← {t("Terug", "Back")}
        </button>
        <h2
          style={{
            fontFamily: "'Montserrat',sans-serif",
            fontSize: 36,
            fontWeight: 800,
            marginBottom: 6,
            color: DT,
          }}
        >
          {t("Deelnemen", "Join")}
        </h2>
        <p
          style={{
            color: accent,
            fontSize: 14,
            marginBottom: 28,
            fontFamily: "'Montserrat',sans-serif",
            fontWeight: 600,
          }}
        >
          {t(
            "Voer de code in die de facilitator met je heeft gedeeld.",
            "Enter the code shared by the facilitator."
          )}
        </p>
        <InputFieldLight
          label={t("Sessiecode", "Session code")}
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder={t("Bijv: ABC123", "E.g.: ABC123")}
          mono
        />
        <InputFieldLight
          label={t("Jouw naam", "Your name")}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("Echte naam of alias?", "Real name or alias?")}
        />
        {err && (
          <div
            style={{
              background: C.red + "20",
              border: `1px solid ${C.red}50`,
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 14,
            }}
          >
            <p
              style={{
                color: C.red,
                fontSize: 13,
                fontWeight: 700,
                fontFamily: "'Montserrat',sans-serif",
              }}
            >
              {err}
            </p>
          </div>
        )}
        <Btn size="lg" full color={accent} disabled={busy} onClick={join}>
          {busy ? t("Verbinden…", "Connecting…") : t("Deelnemen →", "Join →")}
        </Btn>
      </div>
    </div>
  );
}

// ─── DEELNEMER: SESSIE ────────────────────────────────────────────────
function PSession({ code, myId, myName, onBack }) {
  const t = useT();
  const TL = useTL();
  const [sess, setSess] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const textRef = useRef("");
  const sessRef = useRef(null);

  useEffect(() => {
    textRef.current = text;
  }, [text]);
  useEffect(() => {
    sessRef.current = sess;
  }, [sess]);

  useEffect(() => {
    const poll = async () => {
      const raw = await S.get(`sess:${code}`);
      const s = normalizeSession(raw);
      if (s) setSess(s);
      const a = await S.get(`ans:${code}`);
      if (a) setAnswers(normalizeArr(a));
    };
    poll();
    const id = setInterval(poll, 1000);
    return () => clearInterval(id);
  }, [code]);

  useEffect(() => {
    setText("");
  }, [sess?.roundIndex]);

  const submit = async (forceText) => {
    const answerText = forceText ?? textRef.current;
    if (!answerText?.trim() || saving) return;
    const currentSess = sessRef.current;
    if (!currentSess) return;
    setSaving(true);
    const ans = {
      id: uid(),
      roundIndex: currentSess.roundIndex,
      participantId: myId,
      participantName: myName,
      answer: answerText.trim(),
      timestamp: Date.now(),
    };
    const existing = normalizeArr(await S.get(`ans:${code}`));
    const upd = [
      ...existing.filter(
        (a) => !(a.roundIndex === ans.roundIndex && a.participantId === myId)
      ),
      ans,
    ];
    await S.set(`ans:${code}`, upd);
    setAnswers(upd);
    setSaving(false);
  };

  useEffect(() => {
    if (sess?.roundState !== "done") return;
    const hasAns = answers.some(
      (a) => a.roundIndex === sess.roundIndex && a.participantId === myId
    );
    if (!hasAns && textRef.current.trim()) submit(textRef.current);
  }, [sess?.roundState]);

  if (!sess)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: C.bg,
        }}
      >
        <div style={{ textAlign: "center" }}>
          <Spinner color={C.pink} />
          <p style={{ color: DM, fontFamily: "'Montserrat',sans-serif" }}>
            {t("Verbinden…", "Connecting…")}
          </p>
        </div>
      </div>
    );

  if (sess.status === "lobby")
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: C.bg,
          position: "relative",
        }}
      >
        <Clouds />
        <div
          style={{
            textAlign: "center",
            maxWidth: 420,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Spinner color={C.pink} />
          <h3
            style={{
              fontFamily: "'Montserrat',sans-serif",
              fontSize: 26,
              fontWeight: 800,
              color: DT,
            }}
          >
            {t(`Je bent ingelogd, ${myName}!`, `You're logged in, ${myName}!`)}
          </h3>
          <p
            style={{
              color: sub,
              marginTop: 10,
              fontSize: 15,
              fontFamily: "'Montserrat',sans-serif",
              fontWeight: 600,
            }}
          >
            {t(
              "Wachten tot de facilitator de sessie start…",
              "Waiting for the facilitator to start the session…"
            )}
          </p>
          <div
            style={{
              marginTop: 20,
              padding: "12px 18px",
              background: C.green + "15",
              border: `1.5px solid ${C.green}40`,
              borderRadius: 10,
              color: C.neonblue,
              fontSize: 14,
              fontWeight: 700,
              fontFamily: "'Montserrat',sans-serif",
            }}
          >
            ✓ {t(`Ingelogd als ${myName}`, `Logged in as ${myName}`)}
          </div>
          <p
            style={{
              color: DT,
              fontSize: 12,
              marginTop: 12,
              fontFamily: "'Montserrat',sans-serif",
            }}
          >
            {t("Aanwezig:", "Present:")}{" "}
            {sess.participants?.map((p) => p.name).join(", ")}
          </p>
        </div>
      </div>
    );

  if (sess.status === "finished")
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: C.bg,
          position: "relative",
        }}
      >
        <Clouds />
        <Ball
          size={48}
          color={C.red}
          highlight="#ff8c8c"
          style={{ top: 80, left: 80 }}
        />
        <Ball
          size={56}
          color={C.yellow}
          highlight="#fff0a0"
          style={{ bottom: 100, right: 80 }}
        />
        <Ball
          size={36}
          color="#7B4FBE"
          highlight="#c49be8"
          style={{ top: 160, right: 130 }}
        />
        <Ball
          size={42}
          color={C.green}
          highlight="#7ddd7d"
          style={{ bottom: 180, left: 120 }}
        />
        <Ball
          size={22}
          color={C.pink}
          highlight="#ffb8d4"
          style={{ top: 120, left: 220 }}
        />
        <Ball
          size={28}
          color={C.neonblue}
          highlight="#a8f0fa"
          style={{ bottom: 60, right: 220 }}
        />
        <div style={{ textAlign: "center", position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
          <h2
            style={{
              fontFamily: "'Montserrat',sans-serif",
              fontSize: 32,
              fontWeight: 800,
              color: DT,
            }}
          >
            {t("Sessie afgerond!", "Session finished!")}
          </h2>
          <p
            style={{
              color: sub,
              marginTop: 10,
              fontSize: 16,
              fontFamily: "'Montserrat',sans-serif",
              fontWeight: 600,
            }}
          >
            {t(
              `Bedankt voor je deelname, ${myName}!`,
              `Thanks for participating, ${myName}!`
            )}
          </p>
        </div>
      </div>
    );

  const cur = sess.rounds?.[sess.roundIndex];
  const hasAns = answers.some(
    (a) => a.roundIndex === sess.roundIndex && a.participantId === myId
  );
  const buildSrc =
    cur?.type === "build" ? getBuildSource(sess, answers, myId) : null;

  if (sess.roundState === "waiting")
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: C.bg,
          position: "relative",
        }}
      >
        <Clouds />
        <div
          style={{
            textAlign: "center",
            maxWidth: 440,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Spinner color={C.pink} />
          <h3
            style={{
              fontFamily: "'Montserrat',sans-serif",
              fontSize: 24,
              fontWeight: 800,
              color: DT,
            }}
          >
            {t("Ronde", "Round")} {sess.roundIndex + 1}{" "}
            {t("start zo…", "starting soon…")}
          </h3>
          <p
            style={{
              color: sub,
              fontSize: 14,
              marginTop: 8,
              fontFamily: "'Montserrat',sans-serif",
              fontWeight: 600,
            }}
          >
            {t(
              "De facilitator start de ronde.",
              "The facilitator will start the round."
            )}
          </p>
          <div style={{ marginTop: 24 }}>
            <ProgressBar current={sess.roundIndex} total={sess.rounds.length} />
          </div>
        </div>
      </div>
    );

  if (hasAns)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          background: C.bg,
          position: "relative",
        }}
      >
        <Clouds />
        <div
          style={{
            textAlign: "center",
            maxWidth: 460,
            position: "relative",
            zIndex: 1,
          }}
        >
          <div style={{ fontSize: 56, marginBottom: 14, color: C.neonblue }}>
            ✓
          </div>
          <h3
            style={{
              fontFamily: "'Montserrat',sans-serif",
              fontSize: 26,
              fontWeight: 800,
              color: C.neonblue,
            }}
          >
            {t("Antwoord ingediend!", "Answer submitted!")}
          </h3>
          <p
            style={{
              color: sub,
              marginTop: 8,
              fontSize: 15,
              fontFamily: "'Montserrat',sans-serif",
              fontWeight: 600,
            }}
          >
            {sess.roundState === "done"
              ? t(
                  "Ronde klaar — facilitator gaat zo door.",
                  "Round done — facilitator will continue shortly."
                )
              : t("Goed! Wachten op de anderen…", "Nice! Waiting for others…")}
          </p>
          <div style={{ marginTop: 28 }}>
            <Clock sess={sess} />
          </div>
          <div style={{ marginTop: 20 }}>
            <ProgressBar current={sess.roundIndex} total={sess.rounds.length} />
          </div>
        </div>
      </div>
    );

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: C.bg,
        position: "relative",
      }}
    >
      <Clouds />
      <div
        style={{
          padding: "16px 28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: `1px solid ${C.border}`,
          background: C.surface,
          position: "relative",
          zIndex: 1,
        }}
      >
        <button
          onClick={onBack}
          style={{
            background: "none",
            border: "none",
            color: C.muted,
            cursor: "pointer",
            fontSize: 12,
            fontFamily: "'Montserrat',sans-serif",
            fontWeight: 700,
            letterSpacing: 0.5,
            flexShrink: 0,
            padding: 0,
          }}
        >
          ← {t("Verlaat sessie", "Leave session")}
        </button>
        <div style={{ flex: 1, maxWidth: 400, margin: "0 20px" }}>
          <ProgressBar current={sess.roundIndex} total={sess.rounds.length} />
        </div>
        <div style={{ flexShrink: 0 }}>
          <Clock sess={sess} />
        </div>
      </div>
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "clamp(16px,4vw,40px) clamp(16px,4vw,32px)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ width: "100%", maxWidth: 760 }}>
          <div style={{ marginBottom: 24 }}>
            <Badge color={TC[cur.type]}>
              {TI[cur.type]} {TL[cur.type]}
            </Badge>
          </div>
          {cur.title && (
            <p
              style={{
                fontFamily: "'Montserrat',sans-serif",
                fontSize: 15,
                fontWeight: 700,
                color: sub,
                marginBottom: 10,
                letterSpacing: 0.3,
              }}
            >
              {cur.title}
            </p>
          )}
          {sess.roundState === "done" && (
            <div
              style={{
                marginBottom: 22,
                padding: "10px 16px",
                background: C.red + "20",
                border: `1.5px solid ${C.red}50`,
                borderRadius: 8,
                color: C.red,
                fontSize: 13,
                fontWeight: 700,
                fontFamily: "'Montserrat',sans-serif",
              }}
            >
              ⏰{" "}
              {t(
                "Tijd is om — wordt automatisch ingediend.",
                "Time's up — being submitted automatically."
              )}
            </div>
          )}
          {cur.type === "perspective" && cur.hint && (
            <div
              style={{
                marginBottom: 24,
                padding: "14px 20px",
                background: sub + "18",
                border: `1.5px solid ${sub}40`,
                borderRadius: 12,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: sub,
                  textTransform: "uppercase",
                  letterSpacing: 1.5,
                  marginBottom: 6,
                  fontFamily: "'Montserrat',sans-serif",
                  fontWeight: 700,
                }}
              >
                {t("Perspectief-instructie", "Perspective instruction")}
              </div>
              <div
                style={{
                  color: "#ffffff",
                  fontSize: 15,
                  fontFamily: "'Montserrat',sans-serif",
                  fontWeight: 500,
                }}
              >
                {cur.hint}
              </div>
            </div>
          )}
          <h1
            style={{
              fontFamily: "'Montserrat',sans-serif",
              fontSize: "clamp(28px,5vw,52px)",
              fontWeight: 800,
              lineHeight: 1.2,
              color: "#ffffff",
              marginBottom: 36,
              letterSpacing: -1,
            }}
          >
            {cur.question}
          </h1>
          {cur.type === "build" && (
            <div
              style={{
                marginBottom: 28,
                padding: 20,
                background: buildSrc ? C.pink + "15" : C.surface,
                border: `1.5px solid ${buildSrc ? C.pink + "60" : C.border}`,
                borderRadius: 14,
              }}
            >
              {buildSrc ? (
                <>
                  <div
                    style={{
                      fontSize: 10,
                      color: C.pink,
                      textTransform: "uppercase",
                      letterSpacing: 1.5,
                      marginBottom: 12,
                      fontWeight: 700,
                      fontFamily: "'Montserrat',sans-serif",
                    }}
                  >
                    ⊕{" "}
                    {t(
                      `Bouw voort op het antwoord van ${buildSrc.participantName}`,
                      `Build on the answer from ${buildSrc.participantName}`
                    )}
                  </div>
                  <p
                    style={{
                      fontSize: 16,
                      lineHeight: 1.75,
                      color: "#ffffff",
                      fontStyle: "italic",
                      background: C.pink + "10",
                      padding: "14px 18px",
                      borderRadius: 8,
                      borderLeft: `4px solid ${C.pink}`,
                      fontFamily: "'Montserrat',sans-serif",
                      fontWeight: 500,
                    }}
                  >
                    "{buildSrc.answer}"
                  </p>
                  <div
                    style={{
                      fontSize: 12,
                      color: C.muted,
                      marginTop: 8,
                      fontFamily: "'Montserrat',sans-serif",
                    }}
                  >
                    — {buildSrc.participantName}
                  </div>
                </>
              ) : (
                <p
                  style={{
                    color: C.muted,
                    fontSize: 14,
                    fontFamily: "'Montserrat',sans-serif",
                  }}
                >
                  {t(
                    "Geen vorig antwoord beschikbaar — beantwoord de vraag zelfstandig.",
                    "No previous answer available — answer independently."
                  )}
                </p>
              )}
            </div>
          )}
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
            placeholder={t(
              `Jouw antwoord, ${myName}…`,
              `Your answer, ${myName}…`
            )}
            rows={5}
            style={{
              width: "100%",
              padding: "18px 20px",
              background: C.input,
              border: `2px solid ${text.trim() ? TC[cur.type] : C.border}`,
              borderRadius: 14,
              color: "#ffffff",
              fontSize: "clamp(15px,4vw,17px)",
              fontFamily: "'Montserrat',sans-serif",
              fontWeight: 500,
              lineHeight: 1.65,
              resize: "vertical",
              outline: "none",
              display: "block",
              marginBottom: 20,
              boxSizing: "border-box",
              transition: "border-color .2s",
              minHeight: 120,
            }}
          />
          <Btn
            size="lg"
            full
            color={accent}
            disabled={!text.trim() || saving}
            onClick={() => submit()}
          >
            {saving
              ? t("Opslaan…", "Saving…")
              : t("Antwoord indienen →", "Submit answer →")}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ─── APP ROOT ─────────────────────────────────────────────────────────
export default function App() {
  const urlCode =
    new URLSearchParams(window.location.search).get("code")?.toUpperCase() ||
    "";
  const urlAdmin = new URLSearchParams(window.location.search).has("admin");

  const [lang, setLang] = useState("nl");
  const [screen, setScreen] = useState(
    urlAdmin ? "admin" : urlCode ? "p-join" : "landing"
  );
  const [joinCode, setJoinCode] = useState(urlCode);
  const [sess, setSess] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [code, setCode] = useState("");
  const [myId, setMyId] = useState("");
  const [myName, setMyName] = useState("");

  useEffect(() => {
    if (!["f-lobby", "f-live"].includes(screen) || !code) return;
    const poll = async () => {
      const raw = await S.get(`sess:${code}`);
      const s = normalizeSession(raw);
      if (!s) return;
      setSess(s);
      const a = await S.get(`ans:${code}`);
      if (a) setAnswers(normalizeArr(a));
      if (s.status === "active" && screen === "f-lobby") setScreen("f-live");
      if (s.status === "finished" && screen === "f-live") setScreen("results");
    };
    poll();
    const id = setInterval(poll, 1000);
    return () => clearInterval(id);
  }, [screen, code]);

  useEffect(() => {
    if (screen !== "f-live" || !sess || sess.roundState !== "running") return;
    const rem = msRemaining(sess);
    if (rem <= 0) {
      const upd = { ...sess, roundState: "done" };
      setSess(upd);
      S.set(`sess:${code}`, upd);
      return;
    }
    const timer = setTimeout(() => {
      setSess((prev) => {
        if (!prev || prev.roundState !== "running") return prev;
        const upd = { ...prev, roundState: "done" };
        S.set(`sess:${code}`, upd);
        return upd;
      });
    }, rem);
    return () => clearTimeout(timer);
  }, [screen, sess?.roundState, sess?.startedAt, sess?.roundIndex]);

  const openSession = (s) => {
    const ns = normalizeSession(s);
    setCode(ns.code);
    setSess(ns);
    if (ns.status === "lobby") setScreen("f-lobby");
    else if (ns.status === "active") setScreen("f-live");
    else if (ns.status === "finished") setScreen("results");
  };

  const reset = () => {
    setSess(null);
    setAnswers([]);
    setCode("");
    setMyId("");
    setMyName("");
    setScreen("landing");
  };

  return (
    <LangCtx.Provider value={lang}>
      <SetLangCtx.Provider value={setLang}>
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap"
        />
        <style>{`
          *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
          html, body { background:${C.bg}; }
          textarea, input { font-family:'Montserrat',sans-serif; }
          textarea::placeholder, input::placeholder { color:rgba(255,255,255,0.35); }
          .light-input::placeholder { color:rgba(255,255,255,0.25); }
          .light-input { -webkit-text-fill-color:#ffffff !important; caret-color:#ffffff; }
          @keyframes spin  { to { transform:rotate(360deg); } }
          @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.35} }
          @keyframes blink { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(1.05)} }
          ::-webkit-scrollbar { width:4px; }
          ::-webkit-scrollbar-thumb { background:${C.border}; border-radius:2px; }
        `}</style>

        {/* Control bar — taal + thema */}
        <ControlBar />

        <div
          style={{
            minHeight: "100vh",
            background: C.bg,
            color: C.text,
            fontFamily: "'Montserrat',sans-serif",
            position: "relative",
          }}
        >
          {screen === "landing" && (
            <Landing
              goFacilitator={() => setScreen("f-setup")}
              goJoin={() => setScreen("p-join")}
              goResume={() => setScreen("f-resume")}
            />
          )}
          {screen === "admin" && (
            <Admin
              onBack={() => setScreen("landing")}
              onOpenSession={openSession}
            />
          )}
          {screen === "f-setup" && (
            <FSetup
              onBack={() => setScreen("landing")}
              onCreate={(c, s) => {
                setCode(c);
                setSess(s);
                setScreen("f-lobby");
              }}
            />
          )}
          {screen === "f-resume" && (
            <FResume
              onBack={() => setScreen("landing")}
              onFound={(s) => openSession(s)}
            />
          )}
          {screen === "f-lobby" && sess && (
            <FLobby
              sess={sess}
              setSess={setSess}
              code={code}
              onEnd={reset}
              onBack={reset}
            />
          )}
          {screen === "f-live" && sess && (
            <FLive
              sess={sess}
              setSess={setSess}
              answers={answers}
              code={code}
              onEnd={reset}
            />
          )}
          {screen === "results" && sess && (
            <Results sess={sess} answers={answers} onReset={reset} />
          )}
          {screen === "p-join" && (
            <Join
              onBack={() => setScreen("landing")}
              initialCode={joinCode}
              onJoined={(c, id, nm) => {
                setCode(c);
                setMyId(id);
                setMyName(nm);
                setScreen("p-session");
              }}
            />
          )}
          {screen === "p-session" && (
            <PSession
              code={code}
              myId={myId}
              myName={myName}
              onBack={() => setScreen("landing")}
            />
          )}
        </div>
      </SetLangCtx.Provider>
    </LangCtx.Provider>
  );
}
